import { useNavigate, useParams } from 'react-router-dom'
import { API_BASE as BACKEND } from '../../config'
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { getDay, COMPONENTS } from '../data/curriculum'
import { getDetectiveSentences } from '../data/grammarDetectiveBank'
import { getFillGapSentences } from '../data/fillGapBank'
import { useProgress } from '../hooks/useProgress'
import { usePlan, isDayLocked, FREE_DAY_LIMIT } from '../hooks/usePlan'
import WordLookupProvider from '../components/WordLookup'
import { authHeaders } from '../utils/auth'
import { speak, speakAtRate, stopTTS, clearVoiceCache, getRate, getPitch, saveRate, savePitch } from '../utils/tts'
import { readSSEStream } from '../utils/stream'
import { track } from '../../utils/analytics'
import '../EL.css'

const EL = '/english-learning'

/* ─── AI one-shot call via streaming endpoint ─── */
async function aiAsk(userMessage, systemPrompt, history = []) {
  const res = await fetch(`${BACKEND}/english-tutor/stream`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      message: userMessage,
      history: history.map(m => ({ role: m.role, content: m.content })),
      subject_info: systemPrompt
    })
  })
  if (!res.ok) throw new Error(`${res.status}`)
  return readSSEStream(res.body.getReader())
}

/* ─── Inline Markdown renderer ─── */
function renderInlineMd(text) {
  if (!text) return null
  // Split on **...** bold markers
  const parts = text.split(/\*\*(.*?)\*\*/g)
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
  )
}

/* ─── Voice Selector Modal ─── */
function VoiceSelector({ onClose }) {
  const [voices, setVoices] = useState([])
  const [selectedUS, setSelectedUS] = useState(() => localStorage.getItem('noura_voice_us') || '')
  const [selectedUK, setSelectedUK] = useState(() => localStorage.getItem('noura_voice_uk') || '')
  const [tab, setTab] = useState('us')
  const [rate, setRate] = useState(() => getRate() ?? 0.9)
  const [pitch, setPitch] = useState(() => getPitch() ?? 1.0)
  const [accentOpen, setAccentOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [unsupported, setUnsupported] = useState(false)

  useEffect(() => {
    if (!window.speechSynthesis) { setLoading(false); setUnsupported(true); return }

    let done = false
    const tryLoad = () => {
      const all = window.speechSynthesis.getVoices() || []
      const en = all.filter(v => v.lang.startsWith('en'))
      if (en.length) { setVoices(en); setLoading(false); done = true }
    }

    // 1. Immediate check
    tryLoad()
    if (done) return

    // 2. voiceschanged event
    window.speechSynthesis.addEventListener('voiceschanged', tryLoad)

    // 3. Speak a short utterance — the modal opened from a user tap so we're inside a gesture
    //    This is required on some Android browsers and iOS Safari to expose the voice list
    try {
      const u = new SpeechSynthesisUtterance('hi')
      u.rate = 10; u.volume = 0.01
      u.onend = tryLoad
      window.speechSynthesis.speak(u)
    } catch (_) {}

    // 4. Poll every 400ms for 4 seconds
    const polls = [400, 800, 1200, 1800, 2500, 3500].map(ms => setTimeout(tryLoad, ms))

    // 5. Give up after 4s → mark as unsupported
    const giveUp = setTimeout(() => {
      if (!done) { setLoading(false); setUnsupported(true) }
    }, 4000)

    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', tryLoad)
      polls.forEach(clearTimeout)
      clearTimeout(giveUp)
    }
  }, [])

  const usVoices = voices.filter(v => v.lang === 'en-US' || v.lang.startsWith('en-US')).sort((a, b) => {
    const score = v => /online|natural|neural/i.test(v.name) ? 0 : /google/i.test(v.name) ? 1 : 2
    return score(a) - score(b)
  })
  const ukVoices = voices.filter(v => v.lang === 'en-GB' || v.lang.startsWith('en-GB')).sort((a, b) => {
    const score = v => /online|natural|neural/i.test(v.name) ? 0 : /google/i.test(v.name) ? 1 : 2
    return score(a) - score(b)
  })

  const ACCENT_INFO = {
    us: {
      flag: '🇺🇸',
      title: 'اللهجة الأمريكية — General American',
      traits: [
        { sound: '/r/', label: 'حرف R', us: 'يُنطق دائماً في كل مكان — car /kɑːr/', uk: '—' },
        { sound: '/t/', label: 'حرف T', us: 'يتحوّل لـ D بين حرفين مضغوطين — water = "wader"', uk: '—' },
        { sound: '/æ/', label: 'حرف A', us: "أوسع وأطول — \"can't\" /kænt/", uk: '—' },
        { sound: '/ɑː/', label: 'O المفتوحة', us: '"hot" تُنطق /hɑːt/ مع فتح الفم', uk: '—' },
      ],
    },
    uk: {
      flag: '🇬🇧',
      title: 'اللهجة البريطانية — RP (Received Pronunciation)',
      traits: [
        { sound: '/r/', label: 'حرف R', us: '—', uk: 'لا يُنطق في نهاية الكلمة — car /kɑː/ (بدون R)' },
        { sound: '/ɑː/', label: 'حرف A الطويل', us: '—', uk: '"can\'t" تُنطق /kɑːnt/ بالـ A الطويلة' },
        { sound: '/ɒ/', label: 'O المدوّرة', us: '—', uk: '"hot" تُنطق /hɒt/ مع تدوير الشفتين' },
        { sound: '/juː/', label: 'حرف U', us: '—', uk: '"new/tune" تُنطق بـ /juː/ وضوحاً' },
      ],
    },
  }

  const preview = (v) => {
    window.speechSynthesis.cancel()
    const text = tab === 'us'
      ? "Hello! Water, better, car — that's how I sound in American English."
      : "Hello! Water, better, car — that's how I sound in British English."
    const u = new SpeechSynthesisUtterance(text)
    u.voice = v; u.lang = v.lang; u.rate = rate; u.pitch = pitch
    window.speechSynthesis.speak(u)
  }

  const badge = (v) => {
    if (/online|natural|neural/i.test(v.name)) return <span className="el-voice-badge natural">⭐ طبيعي</span>
    if (/google/i.test(v.name)) return <span className="el-voice-badge google">🔵 Google</span>
    return <span className="el-voice-badge std">🔧 قياسي</span>
  }

  const save = () => {
    localStorage.setItem('noura_voice_us', selectedUS)
    localStorage.setItem('noura_voice_uk', selectedUK)
    localStorage.setItem('noura_voice_setup', '1')
    saveRate(rate)
    savePitch(pitch)
    clearVoiceCache()
    window.speechSynthesis.cancel()
    onClose()
  }

  const currentVoices = tab === 'us' ? usVoices : ukVoices
  const currentSelected = tab === 'us' ? selectedUS : selectedUK
  const setSelected = tab === 'us' ? setSelectedUS : setSelectedUK

  return (
    <div className="el-voice-overlay" onClick={e => { if (e.target === e.currentTarget) { stopTTS(); onClose() } }}>
      <div className="el-voice-modal">
        <div className="el-voice-header">
          <span className="el-voice-title">🎙️ اختر صوت المساعد</span>
          <button className="el-voice-close" onClick={() => { stopTTS(); onClose() }}>✕</button>
        </div>
        <p className="el-voice-subtitle">اضغط ▶ لسماع أي صوت، ثم اختره — يُحفظ ويُستخدم في كل التطبيق</p>

        <div className="el-voice-tabs">
          <button className={`el-voice-tab${tab === 'us' ? ' active' : ''}`} onClick={() => setTab('us')}>🇺🇸 أمريكي (US)</button>
          <button className={`el-voice-tab${tab === 'uk' ? ' active' : ''}`} onClick={() => setTab('uk')}>🇬🇧 بريطاني (UK)</button>
        </div>

        {/* Collapsible accent info */}
        <button className="el-voice-accent-toggle" onClick={() => setAccentOpen(o => !o)}>
          {ACCENT_INFO[tab].flag} {ACCENT_INFO[tab].title} {accentOpen ? '▲' : '▼'}
        </button>
        {accentOpen && (
          <div className="el-voice-accent-card">
            <div className="el-voice-accent-traits">
              {ACCENT_INFO[tab].traits.map(t => (
                <div key={t.sound} className="el-voice-accent-row">
                  <span className="el-voice-accent-sound">{t.sound}</span>
                  <span className="el-voice-accent-label">{t.label}</span>
                  <span className="el-voice-accent-desc">{tab === 'us' ? t.us : t.uk}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="el-voice-list">
          {voices.length === 0 && (
            <div className="el-voice-empty">
              {loading ? (
                <>
                  <div style={{ fontSize: '1.8rem' }}>⏳</div>
                  <div style={{ color: 'var(--el-muted)', fontSize: '.88rem', direction: 'rtl' }}>جاري تحميل الأصوات...</div>
                </>
              ) : unsupported ? (
                <>
                  <div style={{ fontSize: '2rem' }}>🔊</div>
                  <div style={{ direction: 'rtl', textAlign: 'center', color: 'var(--el-text)', fontWeight: 700, fontSize: '.95rem' }}>
                    متصفحك لا يدعم اختيار الصوت
                  </div>
                  <div style={{ direction: 'rtl', textAlign: 'center', color: 'var(--el-muted)', fontSize: '.82rem', lineHeight: 1.6 }}>
                    الصوت سيعمل تلقائياً بصوت الجهاز الافتراضي.<br/>
                    اضبط السرعة والنبرة أدناه ثم احفظ.
                  </div>
                </>
              ) : null}
            </div>
          )}
          {currentVoices.map(v => (
            <div
              key={v.name}
              className={`el-voice-item${currentSelected === v.name ? ' selected' : ''}`}
              onClick={() => setSelected(v.name)}
            >
              <div className="el-voice-item-left">
                <div className="el-voice-item-check">{currentSelected === v.name ? '✓' : ''}</div>
                <div>
                  <div className="el-voice-item-name">{v.name}</div>
                  <div className="el-voice-item-lang">{v.lang}</div>
                </div>
                {badge(v)}
              </div>
              <button className="el-voice-preview-btn" onClick={e => { e.stopPropagation(); preview(v) }}>▶ استمع</button>
            </div>
          ))}
        </div>

        <div className="el-voice-footer">
          {currentSelected && (
            <div className="el-voice-current">✅ محدد: <strong>{currentSelected}</strong></div>
          )}

          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '.82rem', minWidth: 70, color: 'var(--el-muted)' }}>🐢 السرعة 🐇</span>
              <input type="range" min="0.5" max="1.5" step="0.05" value={rate}
                onChange={e => setRate(Number(e.target.value))}
                style={{ flex: 1 }} />
              <span style={{ fontSize: '.8rem', minWidth: 30, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{rate.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '.82rem', minWidth: 70, color: 'var(--el-muted)' }}>🔈 النبرة 🔊</span>
              <input type="range" min="0.5" max="2.0" step="0.05" value={pitch}
                onChange={e => setPitch(Number(e.target.value))}
                style={{ flex: 1 }} />
              <span style={{ fontSize: '.8rem', minWidth: 30, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pitch.toFixed(2)}</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <button className="el-nav-btn primary" onClick={save}>💾 حفظ وتطبيق</button>
            <button className="el-nav-btn" onClick={() => { stopTTS(); onClose() }}>تخطي</button>
          </div>
        </div>
      </div>
    </div>
  )
}

/* hook for components: returns [playingKey, triggerSpeak] */
function useTTS() {
  const [playingKey, setPlayingKey] = useState(null)
  const trigger = (text, lang = 'en-US', key) => {
    const k = key || `${lang}:${text.slice(0, 30)}`
    if (playingKey === k) { stopTTS(); setPlayingKey(null); return }
    setPlayingKey(k)
    speak(text, lang, null, () => setPlayingKey(null))
  }
  return [playingKey, trigger]
}

/* small TTS button: shows 🔊 idle / ⏳ loading / ⏹ playing */
function TTSBtn({ text, lang = 'en-US', ttsKey, playingKey, trigger, label }) {
  const k = ttsKey || `${lang}:${text?.slice(0, 30)}`
  const isPlaying = playingKey === k
  return (
    <button
      className={`el-tts-btn${isPlaying ? ' playing' : ''}`}
      onClick={() => trigger(text, lang, k)}
      title={lang === 'en-GB' ? 'British English' : 'American English'}
    >
      {isPlaying ? '⏹' : label || (lang === 'en-GB' ? 'UK 🔊' : 'US 🔊')}
    </button>
  )
}

/* Self-contained dual-accent speak buttons — manages its own playing state */
function DualSpeak({ text, style }) {
  const [playingKey, trigger] = useTTS()
  if (!text) return null
  const kUS = `ds-us:${text.slice(0, 25)}`
  const kUK = `ds-uk:${text.slice(0, 25)}`
  return (
    <span style={{ display: 'inline-flex', gap: 3, verticalAlign: 'middle', ...style }}>
      <button className={`el-speak-btn${playingKey === kUS ? ' playing' : ''}`} onClick={() => trigger(text, 'en-US', kUS)} title="American English">🇺🇸</button>
      <button className={`el-speak-btn${playingKey === kUK ? ' playing' : ''}`} onClick={() => trigger(text, 'en-GB', kUK)} title="British English">🇬🇧</button>
    </span>
  )
}


export default function ELComponentPage({ darkMode, setDarkMode }) {
  const { levelId, dayId, componentId } = useParams()
  const navigate = useNavigate()
  const progress = useProgress()
  const { plan, loading: planLoading } = usePlan()
  const day = getDay(levelId, Number(dayId))

  // pre-load browser voices so TTSBtn can pick the right accent immediately
  useEffect(() => {
    if (!window.speechSynthesis) return
    if (window.speechSynthesis.getVoices().length > 0) return
    const load = () => window.speechSynthesis.getVoices()
    window.speechSynthesis.addEventListener('voiceschanged', load)
    return () => window.speechSynthesis.removeEventListener('voiceschanged', load)
  }, [])
  const compIndex = COMPONENTS.findIndex(c => c.id === componentId)
  const comp = COMPONENTS[compIndex]

  const [buddyMessages, setBuddyMessages] = useState([])
  const [buddyInput, setBuddyInput] = useState('')
  const [avatarState, setAvatarState] = useState('idle')
  const [showXP, setShowXP] = useState(false)
  const [donePending, setDonePending] = useState(false)
  const [showVoicePicker, setShowVoicePicker] = useState(false)

  const doneNavRef = useRef(null)

  // Reset donePending when navigating between components/days (component stays mounted)
  useEffect(() => {
    setDonePending(false)
    doneNavRef.current = null
  }, [levelId, dayId, componentId]) // eslint-disable-line react-hooks/exhaustive-deps

  // C-4 fix: fire navigation after XP animation
  useEffect(() => {
    if (!donePending || !doneNavRef.current) return
    const { progressKey, componentId: cId, next } = doneNavRef.current
    const t = setTimeout(() => {
      progress.markDone(progressKey)
      progress.addXP?.(cId)
      if (next) navigate(next)
      else navigate(`${EL}/level/${levelId}/day/${dayId}`)
    }, 1200)
    return () => clearTimeout(t)
  }, [donePending, progress]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!day || !comp) return <div className={`el-app${darkMode ? ' el-dark' : ''}`}><div className="el-page"><p style={{ padding: 32 }}>Not found.</p></div></div>

  if (!planLoading && isDayLocked(dayId, plan)) {
    return (
      <div className={`el-app${darkMode ? ' el-dark' : ''}`}>
        <div className="el-page">
          <header className="el-top-bar">
            <button className="el-icon-btn" onClick={() => navigate(`${EL}/level/${levelId}/day/${dayId}`)}>←</button>
            <span className="el-top-bar-title">Day {day.id} — {levelId}</span>
          </header>
          <div style={{ textAlign: 'center', padding: '60px 24px' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>🔒</div>
            <h2 style={{ marginBottom: 8 }}>هذا الدرس لـ Premium</h2>
            <p style={{ color: 'var(--el-muted, #888)', marginBottom: 20 }}>
              أول {FREE_DAY_LIMIT} أيام من كل مستوى مجانية. رقّي حسابك لفتح باقي الأيام.
            </p>
            <button className="el-nav-btn primary" onClick={() => navigate('/')}>💎 ترقية لـ Premium</button>
          </div>
        </div>
      </div>
    )
  }

  const progressKey = `${levelId}-${dayId}-${componentId}`
  const done = progress.isDone(progressKey)

  const handleDone = () => {
    if (!done) {
      const next = COMPONENTS[compIndex + 1]
      doneNavRef.current = {
        progressKey,
        componentId,
        next: next ? `${EL}/level/${levelId}/day/${dayId}/${next.id}` : null,
      }
      setShowXP(true)
      setDonePending(true)
    } else {
      const next = COMPONENTS[compIndex + 1]
      if (next) navigate(`${EL}/level/${levelId}/day/${dayId}/${next.id}`)
      else navigate(`${EL}/level/${levelId}/day/${dayId}`)
    }
  }

  return (
    <div className={`el-app${darkMode ? ' el-dark' : ''}`}>
      <WordLookupProvider>
      {showXP && <XPPopAnimation onDone={() => setShowXP(false)} />}
      {showVoicePicker && <VoiceSelector onClose={() => setShowVoicePicker(false)} />}
      <div className="el-page el-comp-page">
        <header className="el-top-bar">
          <button className="el-icon-btn" onClick={() => navigate(`${EL}/level/${levelId}/day/${dayId}`)}>←</button>
          <span className="el-top-bar-title">{comp.icon} {comp.labelEn}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="el-icon-btn" onClick={() => setShowVoicePicker(true)} title="اختر الصوت">🎙️</button>
          </div>
        </header>

        <div className="el-comp-progress-strip">
          {COMPONENTS.map((c, i) => (
            <div
              key={c.id}
              className={'el-strip-dot' + (i === compIndex ? ' active' : progress.isDone(`${levelId}-${dayId}-${c.id}`) ? ' done' : '')}
              onClick={() => navigate(`${EL}/level/${levelId}/day/${dayId}/${c.id}`)}
              title={c.labelEn}
            />
          ))}
        </div>

        <div className="el-study-room">
          <div className="el-content-panel">
            <div className="el-comp-body">
              {componentId === 'vocab'     && <VocabComp day={day} levelId={levelId} dayId={dayId} progress={progress} setAvatarState={setAvatarState} setBuddyMessages={setBuddyMessages} />}
              {componentId === 'grammar'   && <GrammarComp day={day} levelId={levelId} setBuddyMessages={setBuddyMessages} setAvatarState={setAvatarState} />}
              {componentId === 'reading'   && <ReadingComp day={day} levelId={levelId} dayId={dayId} setAvatarState={setAvatarState} setBuddyMessages={setBuddyMessages} />}
              {componentId === 'listening' && <ListeningComp day={day} levelId={levelId} dayId={dayId} setAvatarState={setAvatarState} setBuddyMessages={setBuddyMessages} />}
              {componentId === 'shadowing' && <ShadowingComp day={day} levelId={levelId} />}
              {componentId === 'writing'   && <WritingComp day={day} levelId={levelId} dayId={dayId} navigate={navigate} setBuddyMessages={setBuddyMessages} setBuddyInput={setBuddyInput} setAvatarState={setAvatarState} progress={progress} />}
            </div>

            <div className="el-comp-footer">
              {compIndex > 0 && (
                <button className="el-nav-btn" onClick={() => navigate(`${EL}/level/${levelId}/day/${dayId}/${COMPONENTS[compIndex - 1].id}`)}>
                  ← السابق
                </button>
              )}
              <button
                className="el-later-btn"
                onClick={() => navigate(`${EL}/level/${levelId}/day/${dayId}`)}
                title="حفظ التقدم والعودة لاحقاً"
              >
                ⏸ أكمل لاحقاً
              </button>
              <button className={'el-nav-btn primary' + (done ? ' already-done' : '')} onClick={handleDone}>
                {done ? '✓ مكتمل' : compIndex < COMPONENTS.length - 1 ? 'أكملت ← التالي' : 'أكملت اليوم ✓'}
              </button>
            </div>
          </div>
        </div>
      </div>
      </WordLookupProvider>
    </div>
  )
}

/* ─── Vocabulary ─── */
function VocabComp({ day, levelId, dayId, progress, setAvatarState, setBuddyMessages }) {
  const { vocabulary: v } = day
  const [playingKey, triggerBase] = useTTS()
  const trigger = (text, lang, key) => {
    setAvatarState?.('speaking')
    triggerBase(text, lang, key)
  }
  const [viewMode, setViewMode] = useState('table')   // 'table' | 'cards'
  const [flipped, setFlipped] = useState({})
  const [revealCount, setRevealCount] = useState(5)   // step-by-step reveal
  const total = v.words.length
  const showAll = revealCount >= total

  const toggleFlip = (i) => setFlipped(f => ({ ...f, [i]: !f[i] }))

  return (
    <div className="el-section">
      <div className="el-section-intro">{v.intro}</div>

      {/* view toggle */}
      <div className="el-vocab-view-toggle">
        <button className={'el-view-btn' + (viewMode === 'table' ? ' active' : '')} onClick={() => setViewMode('table')}>📋 جدول</button>
        <button className={'el-view-btn' + (viewMode === 'cards' ? ' active' : '')} onClick={() => setViewMode('cards')}>🃏 بطاقات</button>
        <span className="el-hard-count">
          ⭐ {v.words.filter((w,i) => progress.isHardWord(w.word, levelId, dayId)).length} / {v.words.length} صعبة
        </span>
      </div>

      {/* accent legend */}
      <div className="el-tts-legend">
        <span>US = American English</span>
        <span>UK = British English</span>
        <span style={{ opacity: .6, fontSize: '0.8rem' }}>اضغط مرة ثانية لإيقاف الصوت ⏹</span>
      </div>

      {viewMode === 'table' && (
        <div className="el-vocab-table-wrap">
          <table className="el-vocab-table">
            <thead>
              <tr>
                <th>الكلمة</th>
                <th>US / UK</th>
                <th>IPA</th>
                <th>المعنى</th>
                <th>النطق بالعربي</th>
                <th>مثال</th>
                <th>الترجمة</th>
                <th>⭐</th>
              </tr>
            </thead>
            <tbody>
              {v.words.slice(0, revealCount).map((w, i) => {
                const hard = progress.isHardWord(w.word, levelId, dayId)
                return (
                  <tr key={i} className={hard ? 'el-row-hard' : ''}>
                    <td><strong className="el-word-highlight">{w.word}</strong></td>
                    <td>
                      <div className="el-tts-pair">
                        <TTSBtn text={w.word} lang="en-US" ttsKey={`us-word-${i}`} playingKey={playingKey} trigger={trigger} />
                        <TTSBtn text={w.word} lang="en-GB" ttsKey={`gb-word-${i}`} playingKey={playingKey} trigger={trigger} />
                      </div>
                    </td>
                    <td><span className="el-ipa">{w.ipa}</span></td>
                    <td>{w.arabic}</td>
                    <td><span className="el-phonetic">{w.phonetic}</span></td>
                    <td>
                      <em>{w.example}</em>
                      <div className="el-tts-pair" style={{ marginTop: 4 }}>
                        <TTSBtn text={w.example} lang="en-US" ttsKey={`us-ex-${i}`} playingKey={playingKey} trigger={trigger} label="US جملة" />
                        <TTSBtn text={w.example} lang="en-GB" ttsKey={`gb-ex-${i}`} playingKey={playingKey} trigger={trigger} label="UK جملة" />
                      </div>
                    </td>
                    <td className="el-text-muted">{w.exampleAr}</td>
                    <td>
                      <button
                        className={'el-hard-btn' + (hard ? ' active' : '')}
                        onClick={() => progress.toggleHardWord(w, levelId, dayId)}
                        title={hard ? 'إزالة من قائمة الصعب' : 'أضف للكلمات الصعبة'}
                      >{hard ? '⭐' : '☆'}</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {!showAll && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <button className="el-nav-btn primary" onClick={() => setRevealCount(c => Math.min(c + 5, total))}>
                التالي ({Math.min(revealCount, total)} / {total}) ↓
              </button>
              <button className="el-nav-btn" style={{ marginRight: 8 }} onClick={() => setRevealCount(total)}>
                عرض الكل
              </button>
            </div>
          )}
        </div>
      )}

      {viewMode === 'cards' && (
        <div className="el-flashcard-grid">
          <p className="el-cards-hint">اضغط على البطاقة لتقليبها · ⭐ لتحديد الصعبة</p>
          {v.words.slice(0, revealCount).map((w, i) => {
            const hard = progress.isHardWord(w.word, levelId, dayId)
            return (
              <div key={i} className={'el-flashcard' + (flipped[i] ? ' flipped' : '') + (hard ? ' hard' : '') + ' el-fade-in'} onClick={() => toggleFlip(i)}>
                <div className="el-fc-inner">
                  {/* Front */}
                  <div className="el-fc-front">
                    <div className="el-fc-word">{w.word}</div>
                    <div className="el-fc-ipa">{w.ipa}</div>
                    <div className="el-tts-pair" style={{ justifyContent: 'center', marginTop: 8 }} onClick={e => e.stopPropagation()}>
                      <TTSBtn text={w.word} lang="en-US" ttsKey={`fc-us-${i}`} playingKey={playingKey} trigger={trigger} />
                      <TTSBtn text={w.word} lang="en-GB" ttsKey={`fc-gb-${i}`} playingKey={playingKey} trigger={trigger} />
                    </div>
                    <div className="el-fc-hint">اضغط للمعنى ↩</div>
                  </div>
                  {/* Back */}
                  <div className="el-fc-back">
                    <div className="el-fc-arabic">{w.arabic}</div>
                    <div className="el-fc-phonetic">{w.phonetic}</div>
                    <div className="el-tts-pair" style={{ justifyContent: 'center', margin: '6px 0' }} onClick={e => e.stopPropagation()}>
                      <TTSBtn text={w.word} lang="en-US" ttsKey={`fc-back-us-${i}`} playingKey={playingKey} trigger={trigger} />
                      <TTSBtn text={w.word} lang="en-GB" ttsKey={`fc-back-gb-${i}`} playingKey={playingKey} trigger={trigger} />
                    </div>
                    <div className="el-fc-example">{w.example}</div>
                    <div className="el-fc-example-ar">{w.exampleAr}</div>
                  </div>
                </div>
                <button
                  className={'el-hard-btn fc' + (hard ? ' active' : '')}
                  onClick={e => { e.stopPropagation(); progress.toggleHardWord(w, levelId, dayId) }}
                  title={hard ? 'إزالة' : 'صعبة'}
                >{hard ? '⭐' : '☆'}</button>
              </div>
            )
          })}
          {!showAll && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '8px 0' }}>
              <button className="el-nav-btn primary" onClick={() => setRevealCount(c => Math.min(c + 5, total))}>
                عرض 5 كلمات أكثر ({Math.min(revealCount, total)} / {total}) ↓
              </button>
            </div>
          )}
        </div>
      )}

      {/* Teacher Corner + new features */}
      <TeacherCorner words={v.words} dayTitle={day.title} />
      <FillGapExercise words={v.words} allLearnedWords={progress.hardWords} levelId={levelId} dayId={dayId} />
      <VocabStoryGen words={v.words} dayTitle={day.title} levelId={levelId} allLearnedWords={progress.hardWords} />
    </div>
  )
}

/* ─── Teacher Corner: Ask the Teacher about vocabulary ─── */
function TeacherCorner({ words, dayTitle }) {
  const [open, setOpen] = useState(false)
  const [msgs, setMsgs] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const endRef = useRef(null)
  const msgsRef = useRef(msgs) // H-11: always current msgs for chip handlers

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [msgs])
  useEffect(() => { msgsRef.current = msgs }, [msgs])

  const wordList = words.map((w, i) => `${i + 1}. ${w.word} (${w.arabic}) — ${w.ipa}`).join('\n')

  const systemPrompt = `أنت أستاذ لغة إنجليزية متمرس ومتخصص. درس اليوم: "${dayTitle}". كلمات الدرس: ${wordList}

أسلوب الرد:
- اشرح بالعربية بشكل تفصيلي ومفيد
- استخدم هذا الهيكل دائماً:

**📌 الشرح:**
[شرح مفصّل للنقطة المطلوبة]

**📖 أمثلة:**
[مثال إنجليزي أول] — [معناه بالعربي]
[مثال إنجليزي ثاني] — [معناه بالعربي]

**💡 ملاحظة مهمة:**
[نصيحة أو قاعدة مهمة]

**⚠️ أخطاء شائعة:**
[خطأ شائع] ✗ / [الصواب] ✓

- إذا سُئلت عن كلمة: أعطِ المعنى، النطق، الجذر، المرادفات، والأضداد
- إذا سُئلت عن قاعدة: اشرح متى تُستخدم، والصيغ المختلفة
- لا تختصر على حساب الفائدة
- لا تضف مقدمات أو خواتيم زائدة`

  const send = async () => {
    if (!input.trim() || loading) return
    const userMsg = { role: 'user', content: input.trim() }
    setMsgs(prev => [...prev, userMsg])
    setInput('')
    setLoading(true)
    try {
      const aiReply = await aiAsk(userMsg.content, systemPrompt, msgsRef.current)
      setMsgs(prev => [...prev, { role: 'assistant', content: aiReply || 'ما وصلني رد واضح — جرّبي تعيدي صياغة سؤالك أو اسأليني مرة ثانية 🙏' }])
    } catch {
      setMsgs(prev => [...prev, { role: 'assistant', content: 'تعذّر الاتصال. تحقق من الإنترنت.' }])
    } finally { setLoading(false) }
  }

  return (
    <div className="el-teacher-corner">
      <button className="el-teacher-toggle" onClick={() => setOpen(o => !o)}>
        👨‍🏫 {open ? 'أغلق زاوية الأستاذ' : 'اسأل الأستاذ عن أي كلمة'}
      </button>
      {open && (
        <div className="el-teacher-chat">
          <div className="el-teacher-header">
            <span>👨‍🏫 الأستاذ</span>
            <span style={{ fontSize: '.75rem', color: 'var(--el-muted)' }}>يعرف كل كلمات درس اليوم</span>
          </div>
          {msgs.length === 0 && (
            <div className="el-teacher-suggestions">
              {['ما معنى أول كلمة؟', 'كيف أحفظ الكلمات؟', 'هل يمكنك شرح كلمة صعبة؟'].map(q => (
                <button key={q} className="el-suggestion-chip" onClick={async () => {
                  if (loading) return
                  const history = msgsRef.current // H-11: use ref, not stale closure
                  setMsgs(prev => [...prev, { role: 'user', content: q }])
                  setLoading(true)
                  try {
                    const reply = await aiAsk(q, systemPrompt, history)
                    setMsgs(prev => [...prev, { role: 'assistant', content: reply || 'ما وصلني رد واضح — جرّبي تسألي مرة ثانية 🙏' }])
                  } catch {
                    setMsgs(prev => [...prev, { role: 'assistant', content: 'تعذّر الاتصال.' }])
                  } finally { setLoading(false) }
                }}>
                  {q}
                </button>
              ))}
            </div>
          )}
          <div className="el-teacher-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`el-tc-msg ${m.role}`}>
                {m.role === 'assistant' && <span className="el-tc-avatar">👨‍🏫</span>}
                <span className="el-tc-text">
                  {m.role === 'assistant'
                    ? m.content.split('\n').filter(l => l.trim()).map((line, li) => {
                        const isHeader = /^\*\*[📌📖💡⚠️]/.test(line.trim())
                        const isBullet = /^[-•*]\s/.test(line.trim())
                        const clean = line
                          .replace(/^[-•*]\s/, '')
                          .replace(/\*\*(.*?)\*\*/g, '$1')
                          .trim()
                        if (isHeader) return (
                          <div key={li} style={{ fontWeight:700, color:'var(--el-accent)', marginTop:10, marginBottom:4, fontSize:'.9rem' }}>{clean}</div>
                        )
                        if (isBullet) return (
                          <div key={li} style={{ display:'flex', gap:6, marginBottom:4 }}><span style={{color:'var(--el-accent)',fontWeight:700,flexShrink:0}}>•</span><span>{clean}</span></div>
                        )
                        return <div key={li} style={{ marginBottom:4 }}>{clean}</div>
                      })
                    : m.content}
                </span>
              </div>
            ))}
            {loading && <div className="el-tc-msg assistant"><span className="el-tc-avatar">👨‍🏫</span><span className="el-buddy-typing"><span/><span/><span/></span></div>}
            <div ref={endRef} />
          </div>
          <div className="el-teacher-input-row">
            <input
              className="el-buddy-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
              placeholder="اسأل عن أي كلمة من الدرس..."
              disabled={loading}
            />
            <button className="el-buddy-send" onClick={send} disabled={loading || !input.trim()}>↑</button>
          </div>
        </div>
      )}
    </div>
  )
}

/* C-3: RolePlayMode deleted — full role-play lives in ELRolePlayPage.jsx */

/* ─── Drag-and-Drop sentence builder ─── */
function DragSentence({ question, answer }) {
  const match = question.match(/\(([^)]+)\)/)
  const words = match ? match[1].split(/\s*\/\s*/).map(w => w.trim()).filter(Boolean) : []
  const [shuffled] = useState(() => [...words].sort(() => Math.random() - .5))
  const [bank, setBank] = useState(shuffled)
  const [placed, setPlaced] = useState([])
  const [result, setResult] = useState(null)

  const moveToPlaced = (word, idx) => {
    if (result) return
    setBank(b => b.filter((_, i) => i !== idx))
    setPlaced(p => [...p, word])
    setResult(null)
  }
  const moveToBank = (word, idx) => {
    if (result) return
    setPlaced(p => p.filter((_, i) => i !== idx))
    setBank(b => [...b, word])
    setResult(null)
  }
  const check = () => {
    // Strip ALL punctuation (not just trailing) before comparing — the word
    // bank only contains bare words/phrases, so an answer with internal
    // punctuation (e.g. "I'd like a coffee, please.") would otherwise never
    // match any arrangement of the placed chips.
    const normalize = s => s.replace(/[.!?,]/g, '').trim().toLowerCase().replace(/\s+/g, ' ')
    const sentence = normalize(placed.join(' '))
    const correct = normalize(answer)
    setResult(sentence === correct ? 'correct' : 'wrong')
  }
  const reset = () => { setBank([...words].sort(() => Math.random() - .5)); setPlaced([]); setResult(null) }

  return (
    <div className="el-drag-exercise">
      <div className="el-drag-label">🔀 رتّب الكلمات لتكوين جملة صحيحة:</div>
      <div
        className={'el-drag-drop-zone' + (result === 'correct' ? ' correct' : result === 'wrong' ? ' wrong' : '')}
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault()
          const d = JSON.parse(e.dataTransfer.getData('text/plain'))
          if (d.from === 'bank') moveToPlaced(d.word, d.idx)
        }}
      >
        {placed.length === 0
          ? <span className="el-drag-placeholder">اسحب الكلمات هنا أو اضغط عليها...</span>
          : placed.map((w, i) => (
            <span
              key={`placed-${w}-${i}`} className="el-drag-chip placed"
              draggable
              onDragStart={e => e.dataTransfer.setData('text/plain', JSON.stringify({ word: w, from: 'placed', idx: i }))}
              onClick={() => moveToBank(w, i)}
            >{w}</span>
          ))
        }
      </div>
      <div
        className="el-drag-bank"
        onDragOver={e => e.preventDefault()}
        onDrop={e => {
          e.preventDefault()
          const d = JSON.parse(e.dataTransfer.getData('text/plain'))
          if (d.from === 'placed') moveToBank(d.word, d.idx)
        }}
      >
        {bank.map((w, i) => (
          <span
            key={`bank-${w}-${i}`} className="el-drag-chip"
            draggable
            onDragStart={e => e.dataTransfer.setData('text/plain', JSON.stringify({ word: w, from: 'bank', idx: i }))}
            onClick={() => moveToPlaced(w, i)}
          >{w}</span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="el-nav-btn primary" onClick={check} disabled={!placed.length || !!result}>تحقق ✓</button>
        <button className="el-nav-btn" onClick={reset}>إعادة 🔄</button>
      </div>
      {result === 'correct' && <div className="el-drag-result correct">🎉 ممتاز! الجملة صحيحة.</div>}
      {result === 'wrong' && <div className="el-drag-result wrong">❌ ليس الترتيب الصحيح.<div className="el-exercise-ans" style={{ marginTop: 6 }}>✅ {answer}</div></div>}
    </div>
  )
}

/* ─── Grammar Pattern ─── */
function GrammarPattern({ p, pi, day }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="el-grammar-block">
      <h3 className="el-grammar-name">{p.name}</h3>
      <div className="el-grammar-explain">{renderInlineMd(p.explanation)}</div>
      <div className="el-formula-box">
        <span className="el-formula-label">الصيغة:</span>
        <code className="el-formula">{p.formula}</code>
      </div>
      <div className="el-examples-list">
        {p.examples.map((ex, i) => (
          <div key={i} className="el-example-item">
            ✦ {renderInlineMd(ex)}
            <DualSpeak text={ex.replace(/\*\*/g,'')} style={{ marginRight: 6 }} />
          </div>
        ))}
      </div>
      <button
        className="el-nav-btn"
        style={{ marginTop: 10, fontSize: '.82rem', background: expanded ? 'var(--el-bg3)' : undefined }}
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? '🔼 إخفاء الصيغ' : '📚 جميع الصيغ والأمثلة'}
      </button>
      {expanded && (
        <div className="el-grammar-expanded" style={{ marginTop: 10, padding: '12px 14px', background: 'var(--el-bg3)', borderRadius: 10, fontSize: '.87rem', lineHeight: 1.8 }}>
          <div style={{ fontWeight: 700, color: 'var(--el-accent)', marginBottom: 8, fontSize: '.9rem' }}>📌 الصيغة الكاملة</div>
          <div style={{ marginBottom: 10, padding: '8px 12px', background: 'var(--el-bg2)', borderRadius: 8, fontFamily: 'monospace' }}>{p.formula}</div>

          <div style={{ fontWeight: 700, color: 'var(--el-accent)', marginBottom: 6, fontSize: '.9rem' }}>✅ أمثلة</div>
          {p.examples.map((ex, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <span style={{ color: 'var(--el-accent)', flexShrink: 0 }}>•</span>
              <span>{renderInlineMd(ex)}</span>
              <DualSpeak text={ex.replace(/\*\*/g,'')} style={{ flexShrink: 0 }} />
            </div>
          ))}

          {p.exercises && p.exercises.length > 0 && (
            <>
              <div style={{ fontWeight: 700, color: 'var(--el-accent)', margin: '10px 0 6px', fontSize: '.9rem' }}>📝 نماذج تمارين</div>
              {p.exercises.slice(0, 2).map((ex, i) => (
                <div key={i} style={{ marginBottom: 4, paddingRight: 8, color: 'var(--el-muted)', fontSize: '.83rem' }}>
                  ❓ {ex.question}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── Grammar ─── */
function GrammarComp({ day, levelId, setBuddyMessages, setAvatarState }) {
  const [shown, setShown] = useState({})
  const { grammar: g } = day

  return (
    <div className="el-section">
      {g.patterns.map((p, pi) => (
        <GrammarPattern key={pi} p={p} pi={pi} day={day} />
      ))}
      {g.patterns.map((p, pi) => (
        <div key={`ex-${pi}`} className="el-grammar-block" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
          <div className="el-exercises-title">📝 تمارين — صحّح بنفسك</div>
          {p.exercises.map((ex, i) => {
            const isDrag = /رتّب/.test(ex.question) && /\(/.test(ex.question)
            return (
              <div key={i} className="el-exercise-card">
                {isDrag ? (
                  <DragSentence question={ex.question} answer={ex.answer} />
                ) : (
                  <>
                    <div className="el-exercise-q">❓ {ex.question}</div>
                    <button
                      className={'el-reveal-btn' + (shown[`${pi}-${i}`] ? ' revealed' : '')}
                      onClick={() => setShown(s => ({ ...s, [`${pi}-${i}`]: !s[`${pi}-${i}`] }))}
                    >
                      {shown[`${pi}-${i}`] ? '🙈 إخفاء' : '👁️ الإجابة'}
                    </button>
                    {shown[`${pi}-${i}`] && (
                      <div className="el-exercise-ans">✅ {ex.answer}</div>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      ))}

      <GrammarPatternFlashcards patterns={g.patterns} />
      <GrammarDetective day={day} levelId={levelId} dayId={day?.id} />
    </div>
  )
}

/* ─── Highlighted Text renderer ─── */
function HighlightedText({ text, highlights }) {
  if (!highlights.length) return <>{text}</>
  // merge overlapping, sort by start
  const sorted = [...highlights].sort((a, b) => a.start - b.start)
  const parts = []
  let cursor = 0
  for (const h of sorted) {
    if (h.start > cursor) parts.push({ text: text.slice(cursor, h.start), color: null })
    if (h.end > cursor) {
      parts.push({ text: text.slice(Math.max(h.start, cursor), h.end), color: h.color })
      cursor = h.end
    }
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), color: null })
  return (
    <>
      {parts.map((p, i) =>
        p.color
          ? <mark key={i} style={{ background: HIGHLIGHT_COLORS[p.color]?.bg, borderRadius: 3, padding: '0 1px' }}>{p.text}</mark>
          : <span key={i}>{p.text}</span>
      )}
    </>
  )
}

/* ─── Word Lookup Popup (double-click anywhere in Reading) ─── */
function WordLookupPopup({ word, rect, vocabWords, onClose }) {
  const [playingKey, trigger] = useTTS()
  const clean = word.replace(/[^a-zA-Z'-]/g, '').toLowerCase()
  const match = vocabWords?.find(w => w.word.toLowerCase() === clean) || vocabWords?.find(w => w.word.toLowerCase().split(/[\s/]+/).includes(clean))

  useEffect(() => {
    const close = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [onClose])

  if (!clean) return null

  return (
    <div className="el-lookup-backdrop" onClick={onClose}>
      <div
        className="el-lookup-popup"
        style={{ top: Math.min(rect.bottom + 8, window.innerHeight - 220), left: Math.max(8, Math.min(rect.left, window.innerWidth - 280)) }}
        onClick={e => e.stopPropagation()}
      >
        <button className="el-lookup-close" onClick={onClose}>✕</button>
        <div className="el-lookup-word">{match?.word || word}</div>
        {match ? (
          <>
            <div className="el-lookup-ipa">{match.ipa}</div>
            <div className="el-lookup-arabic">{match.arabic}</div>
            <div className="el-lookup-phonetic">نطق: {match.phonetic}</div>
            <div className="el-tts-pair" style={{ margin: '6px 0' }}>
              <TTSBtn text={match.word} lang="en-US" ttsKey="lu-us" playingKey={playingKey} trigger={trigger} label="US 🔊" />
              <TTSBtn text={match.word} lang="en-GB" ttsKey="lu-gb" playingKey={playingKey} trigger={trigger} label="UK 🔊" />
            </div>
            <div className="el-lookup-example">{match.example}</div>
          </>
        ) : (
          <>
            <div className="el-lookup-arabic" style={{ color: 'var(--el-muted)', fontSize: '.85rem' }}>الكلمة ليست في قاموس اليوم</div>
            <div className="el-tts-pair" style={{ margin: '6px 0' }}>
              <TTSBtn text={word} lang="en-US" ttsKey="lu-us" playingKey={playingKey} trigger={trigger} label="US نطق" />
              <TTSBtn text={word} lang="en-GB" ttsKey="lu-gb" playingKey={playingKey} trigger={trigger} label="UK نطق" />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const HIGHLIGHT_COLORS = {
  yellow: { bg: '#fef08a', label: '⭐ مهم',    dark: '#854d0e' },
  red:    { bg: '#fecaca', label: '❗ صعب',    dark: '#991b1b' },
  green:  { bg: '#bbf7d0', label: '✅ قاعدة',  dark: '#14532d' },
}

/* ─── RSVP Speed Reader ─── */
function RSVPReader({ text, onClose }) {
  const words = text.split(/\s+/).filter(Boolean)
  const [idx, setIdx] = useState(0)
  const [wpm, setWpm] = useState(250)
  const [running, setRunning] = useState(false)
  const timerRef = useRef(null)

  useEffect(() => {
    if (running) {
      timerRef.current = setInterval(() => {
        setIdx(i => {
          if (i >= words.length - 1) { setRunning(false); return i }
          return i + 1
        })
      }, Math.round(60000 / wpm))
    } else {
      clearInterval(timerRef.current)
    }
    return () => clearInterval(timerRef.current)
  }, [running, wpm, words.length])

  const word = words[idx] || ''
  const pct = Math.round((idx / (words.length - 1)) * 100)

  // highlight middle letter
  const mid = Math.floor(word.length / 2)
  const before = word.slice(0, mid)
  const pivot = word[mid] || ''
  const after = word.slice(mid + 1)

  return (
    <div className="el-rsvp-overlay">
      <div className="el-rsvp-box">
        <div className="el-rsvp-progress-bar"><div style={{ width: pct + '%' }} /></div>
        <div className="el-rsvp-word">
          <span className="el-rsvp-before">{before}</span>
          <span className="el-rsvp-pivot">{pivot}</span>
          <span className="el-rsvp-after">{after}</span>
        </div>
        <div className="el-rsvp-counter">{idx + 1} / {words.length}</div>
        <div className="el-rsvp-controls">
          <input type="range" min="100" max="600" step="25" value={wpm}
            onChange={e => setWpm(Number(e.target.value))} />
          <span>{wpm} wpm</span>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
          <button className="el-nav-btn" onClick={() => setIdx(0)}>⏮</button>
          <button className="el-nav-btn primary" onClick={() => setRunning(r => !r)}>
            {running ? '⏸ إيقاف' : idx >= words.length - 1 ? '🔄 إعادة' : '▶ تشغيل'}
          </button>
          <button className="el-nav-btn" onClick={onClose}>✕ إغلاق</button>
        </div>
      </div>
    </div>
  )
}

/* ─── Reading ─── */

function ReadingComp({ day, levelId, dayId }) {
  const [playingKey, trigger] = useTTS()
  const [lookup, setLookup] = useState(null)
  const [hlColor, setHlColor] = useState('yellow')
  const [rsvp, setRsvp] = useState(false)
  const passageRef = useRef(null)
  const sectionRef = useRef(null)
  const [scrollPct, setScrollPct] = useState(0)
  const { reading: r } = day
  const hlKey = `hl-${levelId}-${dayId}`
  const passage = r.passage

  const [highlights, setHighlights] = useState(() => {
    try { return JSON.parse(localStorage.getItem(hlKey) || '[]') } catch { return [] }
  })
  useEffect(() => {
    try { setHighlights(JSON.parse(localStorage.getItem(hlKey) || '[]')) } catch { setHighlights([]) }
  }, [hlKey])
  const saveHL = (arr) => { setHighlights(arr); localStorage.setItem(hlKey, JSON.stringify(arr)) }

  // reading progress bar — the section itself doesn't scroll; the window does
  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current
      if (!el) return
      const { top, height } = el.getBoundingClientRect()
      const scrolled = Math.max(0, -top)
      const scrollable = Math.max(1, height - window.innerHeight)
      setScrollPct(Math.min(100, Math.round((scrolled / scrollable) * 100)))
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleDoubleClick = useCallback(e => {
    const sel = window.getSelection()
    const word = sel?.toString().trim() || ''
    if (!word || word.split(' ').length > 4) return
    const range = sel.getRangeAt(0)
    const rect = range.getBoundingClientRect()
    setLookup({ word, rect })
  }, [])

  return (
    <div className="el-section el-reading-section" ref={sectionRef}>
      <div className="el-reading-prog-bar"><div style={{ width: scrollPct + '%' }} /></div>

      {rsvp && <RSVPReader text={passage} onClose={() => setRsvp(false)} />}

      {lookup && (
        <WordLookupPopup
          word={lookup.word}
          rect={lookup.rect}
          vocabWords={day.vocabulary?.words}
          onClose={() => setLookup(null)}
        />
      )}

      <div className="el-hl-toolbar">
        <span className="el-hl-label">🖊️ تظليل:</span>
        {Object.entries(HIGHLIGHT_COLORS).map(([k, v]) => (
          <button
            key={k}
            className={'el-hl-color-btn' + (hlColor === k ? ' active' : '')}
            style={{ '--hl-bg': v.bg }}
            onClick={() => setHlColor(k)}
            title={v.label}
          >{v.label}</button>
        ))}
        {highlights.length > 0 && (
          <button className="el-hl-clear" onClick={() => saveHL([])}>🗑️ مسح التظليل</button>
        )}
        <button className="el-hl-clear" style={{ marginRight: 'auto' }} onClick={() => setRsvp(true)}>
          ⚡ قراءة سريعة RSVP
        </button>
      </div>

      <div className="el-lookup-hint">💡 انقر مزدوجاً على أي كلمة لمعناها · حدّد نصاً للتظليل</div>

      <div
        className="el-reading-passage"
        ref={passageRef}
        onDoubleClick={handleDoubleClick}
        onMouseUp={() => {
          const sel = window.getSelection()
          const txt = sel?.toString().trim()
          if (!txt || txt.length < 2) return
          const idx = passage.indexOf(txt)
          if (idx === -1) return
          const newHL = [...highlights, { start: idx, end: idx + txt.length, color: hlColor }]
          saveHL(newHL)
          sel.removeAllRanges()
        }}
      >
        <HighlightedText text={passage} highlights={highlights} />
        <div className="el-tts-pair" style={{ marginTop: 10 }}>
          <TTSBtn text={passage} lang="en-US" ttsKey="passage-us" playingKey={playingKey} trigger={trigger} label="US استمع" />
          <TTSBtn text={passage} lang="en-GB" ttsKey="passage-gb" playingKey={playingKey} trigger={trigger} label="UK استمع" />
        </div>
      </div>

      <KeySentences passage={passage} />
      <ReadingBookmarks levelId={levelId} dayId={dayId} />
    </div>
  )
}

/* ─── Key Sentences ─── */
function KeySentences({ passage }) {
  const sentences = (passage || '')
    .replace(/([.!?])\s+/g, '$1\x00')
    .split('\x00')
    .map(s => s.trim())
    .filter(s => s.length > 25 && s.length < 200 && /[a-zA-Z]/.test(s))
    .slice(0, 3)

  if (!sentences.length) return null
  return (
    <div className="el-key-sentences">
      <div className="el-key-sentences-title">⭐ أهم الجمل المفيدة</div>
      {sentences.map((s, i) => (
        <div key={i} className="el-key-sentence-item">
          <div className="el-key-sentence-en">
            {s}
            <DualSpeak text={s} style={{ marginRight: 6 }} />
          </div>
        </div>
      ))}
    </div>
  )
}

/* ─── Listening word targets per level ─── */

/* ─── Listening Speed Control ─── */
function ListeningSpeedControl({ text }) {
  const [rate, setRate] = useState(1)
  const [lang, setLang] = useState('en-US')
  const [playing, setPlaying] = useState(false)
  const speeds = [0.6, 0.75, 1, 1.1, 1.25]
  const labels = ['0.6x', '0.75x', '1x', '1.1x', '1.25x']

  const play = (r) => {
    if (playing) { stopTTS(); setPlaying(false); return }
    setPlaying(true)
    speakAtRate(text, r, lang, () => setPlaying(false))
  }

  return (
    <div className="el-speed-control-wrap">
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <button className={`el-speed-ctrl-btn${lang === 'en-US' ? ' active' : ''}`} onClick={() => setLang('en-US')}>🇺🇸 US</button>
        <button className={`el-speed-ctrl-btn${lang === 'en-GB' ? ' active' : ''}`} onClick={() => setLang('en-GB')}>🇬🇧 UK</button>
      </div>
      <div className="el-speed-control-label">🎚️ سرعة الاستماع:</div>
      <div className="el-speed-control-btns">
        {speeds.map((s, i) => (
          <button key={s} className={`el-speed-ctrl-btn${rate === s ? ' active' : ''}`} onClick={() => setRate(s)}>{labels[i]}</button>
        ))}
      </div>
      <button className={`el-nav-btn${playing ? ' primary' : ''}`} style={{ marginTop: 8 }} onClick={() => play(rate)}>
        {playing ? '⏹ أوقف' : '▶ استمع'}
      </button>
    </div>
  )
}

/* ─── Listening ─── */
function ListeningComp({ day, levelId, dayId }) {
  const { listening: l } = day

  const buildQuestions = (text) => {
    const sentences = text.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 15)
    // C-5 fix: extract real content words for MCQ distractors
    const allWords = text.match(/\b[a-zA-Z]{4,}\b/g) || []
    const uniqueWords = [...new Set(allWords.map(w => w.toLowerCase()))]
      .filter(w => !['that','this','with','have','from','they','were','been','their','when','what','will','your','more','also','into','than','then','some','such','would','could','about','which'].includes(w))

    const makeFill = (sent) => {
      const words = sent.split(' ').filter(w => /^[a-zA-Z]{4,}$/.test(w.replace(/[^a-zA-Z]/g, '')))
      if (!words.length) return null
      const rawTarget = words[Math.floor(words.length * 0.4)] || words[0]
      const target = rawTarget.replace(/[^a-zA-Z]/g, '')
      if (!target) return null
      const escaped = rawTarget.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const q = sent.replace(new RegExp(`\\b${escaped}\\b`, 'i'), '___')
      if (!q.includes('___')) return null
      return { type: 'FILL', q, correct: target }
    }

    const questions = []

    // Words clearly NOT in the passage — used only for T/F "false sentence" swaps
    const notInPassage = ['volcano','satellite','cathedral','microscope','asteroid','parliament']
      .filter(w => !text.toLowerCase().includes(w))

    // MCQ where the blank word AND all 3 distractors are real words that
    // appear somewhere in the passage — a student can't spot the answer by
    // "which word looks foreign," they have to actually remember which word
    // filled that specific blank while listening.
    const usedTargets = new Set()
    const makeFillMCQ = (sent) => {
      const words = sent.split(' ').filter(w => /^[a-zA-Z]{4,}$/.test(w.replace(/[^a-zA-Z]/g, '')))
      if (!words.length) return null
      const rawTarget = words[Math.floor(words.length * 0.4)] || words[0]
      const target = rawTarget.replace(/[^a-zA-Z]/g, '')
      if (!target || usedTargets.has(target.toLowerCase())) return null
      const distractorPool = uniqueWords.filter(w => w !== target.toLowerCase())
      if (distractorPool.length < 3) return null
      const distractors = [...distractorPool].sort(() => Math.random() - .5).slice(0, 3)
      const escaped = rawTarget.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const q = sent.replace(new RegExp(`\\b${escaped}\\b`, 'i'), '___')
      if (!q.includes('___')) return null
      usedTargets.add(target.toLowerCase())
      const options = [target, ...distractors].sort(() => Math.random() - .5)
      return { type: 'MCQ', q: `Complete: "${q.trim()}"`, options, correct: options.indexOf(target) }
    }

    // T/F: one TRUE (exact sentence from passage) + one FALSE (sentence with a key word swapped)
    if (sentences[0]) {
      questions.push({ type: 'TF', q: `True or False: "${sentences[0].slice(0, 80)}"`, correct: 'TRUE' })
    }
    if (sentences[1] && notInPassage.length > 0) {
      // Swap the first long word with a word not in the passage to create a FALSE sentence
      const words = sentences[1].split(' ')
      const swapIdx = words.findIndex(w => w.replace(/[^a-zA-Z]/g, '').length >= 5)
      if (swapIdx !== -1) {
        const falseSent = [...words]
        falseSent[swapIdx] = notInPassage[0]
        questions.push({ type: 'TF', q: `True or False: "${falseSent.join(' ').slice(0, 90)}"`, correct: 'FALSE' })
      } else if (sentences[2]) {
        // fallback: use a different real sentence as TRUE
        questions.push({ type: 'TF', q: `True or False: "${sentences[2].slice(0, 80)}"`, correct: 'TRUE' })
      }
    }

    // FILL: extract real blanks from passage sentences
    for (const sent of sentences.slice(0, 5)) {
      if (questions.filter(q => q.type === 'FILL').length >= 3) break
      const q = makeFill(sent)
      if (q) questions.push(q)
    }

    // MCQ: fill the blank — all 4 options are real words from the passage,
    // so guessing requires actually remembering what was said, not just
    // spotting the one option that "looks foreign."
    for (const sent of sentences.slice(5)) {
      if (questions.filter(q => q.type === 'MCQ').length >= 2) break
      const q = makeFillMCQ(sent)
      if (q) questions.push(q)
    }
    if (questions.filter(q => q.type === 'MCQ').length < 2) {
      for (const sent of sentences) {
        if (questions.filter(q => q.type === 'MCQ').length >= 2) break
        const q = makeFillMCQ(sent)
        if (q) questions.push(q)
      }
    }

    // MCQ: main topic (title-based)
    questions.push({
      type: 'MCQ',
      q: 'What is the main topic of this passage?',
      options: [day.title || 'Daily English', 'Space exploration', 'Ancient history', 'Cooking and recipes'],
      correct: 0,
    })

    // TF: one FALSE — a sentence clearly NOT in the passage
    if (sentences.length >= 2) {
      const notInText = `${sentences[1].split(' ').slice(0, 4).join(' ')} ... is the only topic discussed.`
      questions.push({ type: 'TF', q: `True or False: This passage is only about one single idea with no other details.`, correct: sentences.length > 3 ? 'FALSE' : 'TRUE' })
    }

    // Extra FILL from later sentences
    for (const sent of sentences.slice(3, 8)) {
      if (questions.filter(q => q.type === 'FILL').length >= 4) break
      const q = makeFill(sent)
      if (q) questions.push(q)
    }

    return questions.filter(Boolean).slice(0, 10)
  }

  const [questions] = useState(() => buildQuestions(l.text))
  const [answers, setAnswers] = useState({})
  const [checked, setChecked] = useState(false)
  const [score, setScore] = useState(null)
  const [showText, setShowText] = useState(false)

  const checkAnswers = () => {
    let correct = 0
    questions.forEach((q, i) => {
      const ans = answers[i]
      if (q.type === 'MCQ' && ans === q.correct) correct++
      else if (q.type === 'TF' && ans === q.correct) correct++
      else if (q.type === 'FILL' && typeof ans === 'string' && ans.toLowerCase().trim() === (q.correct || '').toLowerCase().trim()) correct++
    })
    const total = questions.length
    setScore({ correct, total, pct: Math.round((correct / total) * 100) })
    setChecked(true)
  }

  const retry = () => { setAnswers({}); setChecked(false); setScore(null) }
  const allAnswered = questions.every((_, i) => answers[i] !== undefined && answers[i] !== '')

  return (
    <div className="el-section">
      <div className="el-listening-context">🎬 السياق: {l.context}</div>

      <div className="el-listen-player-box">
        <div className="el-listen-player-top">
          <div className="el-listen-player-title">🎧 {day.title}</div>
          <button
            className={`el-listen-text-btn${showText ? ' active' : ''}`}
            onClick={() => setShowText(v => !v)}
          >
            📄 {showText ? 'إخفاء النص' : 'إظهار النص'}
          </button>
        </div>
        {showText && <div className="el-listen-text-reveal">{l.text}</div>}
        <ListeningSpeedControl text={l.text} />
      </div>

      <div className="el-listen-questions-title">📝 أسئلة الاستماع</div>

      {questions.map((q, i) => {
        const ans = answers[i]
        return (
          <div key={i} className="el-listen-q-block">
            <div className="el-listen-q-num">{i + 1}</div>
            <div className="el-listen-q-body">
              {q.type === 'MCQ' && (
                <>
                  <div className="el-listen-q-text">{q.q}</div>
                  <div className="el-listen-mcq-opts">
                    {(q.options || []).map((opt, oi) => {
                      const chosen = ans === oi
                      const isCorrect = checked && oi === q.correct
                      const isWrong   = checked && chosen && oi !== q.correct
                      return (
                        <button key={oi}
                          className={`el-listen-mcq-opt${chosen ? ' chosen' : ''}${isCorrect ? ' correct' : ''}${isWrong ? ' wrong' : ''}`}
                          onClick={() => !checked && setAnswers(a => ({ ...a, [i]: oi }))}
                          disabled={checked}
                        >
                          <span className="el-listen-mcq-circle">{['A','B','C','D'][oi]}</span>
                          {opt}
                        </button>
                      )
                    })}
                  </div>
                  {checked && <div className="el-listen-q-explain">✅ الإجابة: {['A','B','C','D'][q.correct]} — {(q.options||[])[q.correct]}</div>}
                </>
              )}
              {q.type === 'TF' && (
                <>
                  <div className="el-listen-q-text">{q.q}</div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                    {['TRUE','FALSE'].map(val => {
                      const chosen = ans === val
                      const isCorrect = checked && val === q.correct
                      const isWrong   = checked && chosen && val !== q.correct
                      return (
                        <button key={val}
                          className={`el-listen-tf-btn${chosen ? ' chosen' : ''}${isCorrect ? ' correct' : ''}${isWrong ? ' wrong' : ''}`}
                          onClick={() => !checked && setAnswers(a => ({ ...a, [i]: val }))}
                          disabled={checked}
                        >
                          {val === 'TRUE' ? '✓ صحيح' : '✗ خطأ'}
                        </button>
                      )
                    })}
                  </div>
                  {checked && <div className="el-listen-q-explain">✅ الإجابة: {q.correct === 'TRUE' ? 'صحيح' : 'خطأ'}</div>}
                </>
              )}
              {q.type === 'FILL' && (
                <>
                  <div className="el-listen-q-text">
                    {(q.q || '').split('___').map((part, pi, arr) => (
                      <span key={pi}>
                        {part}
                        {pi < arr.length - 1 && (
                          <input
                            className={`el-blank-input${checked ? (String(answers[i]||'').toLowerCase().trim() === String(q.correct||'').toLowerCase().trim() ? ' correct' : ' wrong') : ''}`}
                            value={ans || ''}
                            onChange={e => !checked && setAnswers(a => ({ ...a, [i]: e.target.value }))}
                            placeholder="___"
                            size={Math.max(8, String(q.correct||'').length + 2)}
                            disabled={checked}
                          />
                        )}
                      </span>
                    ))}
                  </div>
                  {checked && <div className="el-listen-q-explain">✅ الإجابة: {q.correct}</div>}
                </>
              )}
            </div>
          </div>
        )
      })}

      {score && (
        <div className={`el-score-banner ${score.pct === 100 ? 'perfect' : score.pct >= 70 ? 'good' : 'retry'}`}>
          {score.pct === 100
            ? `🎉 ممتاز! أجبت على كل الأسئلة بشكل صحيح (${score.total}/${score.total})`
            : score.pct >= 70
              ? `👍 جيد جداً — ${score.correct}/${score.total} صحيحة (${score.pct}%)`
              : `💪 ${score.correct}/${score.total} صحيحة — استمع مجدداً وحاول`
          }
        </div>
      )}
      <div className="el-dictation-btns">
        {!checked
          ? <button className="el-nav-btn primary" onClick={checkAnswers} disabled={!allAnswered}>✅ تحقق من الإجابات</button>
          : <button className="el-nav-btn" onClick={retry}>🔄 حاول مرة ثانية</button>
        }
      </div>
      {score && <AILiveReaction score={score} />}
    </div>
  )
}

/* ─── Shadowing sentences bank — 5 topic-matched sentences per CEFR level ─── */
const SHADOWING_TOPICS = [
  {
    keywords: ['present simple', 'daily routine', 'habits', 'everyday', 'يومي', 'عادات'],
    sentences: {
      A1: ["I wake up at seven every morning.", "She drinks coffee before work.", "We eat lunch at school.", "He walks to the park every day.", "They sleep early on weekdays."],
      A2: ["I usually take the bus to work.", "She always checks her phone in the morning.", "He never skips breakfast on weekdays.", "We sometimes go for a walk after dinner.", "They often watch TV in the evening."],
      B1: ["I tend to spend my evenings reading rather than watching TV.", "She usually prepares her clothes the night before work.", "He rarely misses his morning jog, even in bad weather.", "We generally have a team meeting every Monday morning.", "They always make sure to recycle their household waste."],
      B2: ["I consistently prioritize sleep over late-night socializing.", "She habitually reviews her to-do list before starting her workday.", "He invariably checks the weather forecast before planning outdoor activities.", "We routinely conduct performance reviews at the end of each quarter.", "They persistently maintain healthy eating habits despite busy schedules."],
      C1: ["I've made it a point to dedicate at least an hour each day to professional development.", "She meticulously organizes her schedule to balance work commitments and personal wellbeing.", "He unfailingly adheres to a disciplined morning routine that sets the tone for his entire day.", "We systematically evaluate our processes to ensure continuous improvement across all departments.", "They conscientiously monitor their carbon footprint as part of a broader commitment to sustainability."],
      C2: ["I've cultivated the discipline of deliberate practice, allocating focused blocks of time to skill acquisition rather than passive consumption.", "She orchestrates her professional and personal responsibilities with the precision of a conductor, ensuring neither domain encroaches upon the other.", "He has internalized the philosophy that consistency, however modest, compounds into extraordinary outcomes over time.", "We have institutionalized a culture of reflective practice, wherein team members regularly interrogate assumptions underlying their decisions.", "They navigate the inherent tensions between efficiency and creativity by establishing clear boundaries between structured work and open-ended exploration."],
    }
  },
  {
    keywords: ['past simple', 'past tense', 'history', 'last', 'yesterday', 'ماضي', 'أمس'],
    sentences: {
      A1: ["I went to school yesterday.", "She ate lunch at home.", "We played football last Sunday.", "He watched a film last night.", "They walked to the park."],
      A2: ["I visited my grandmother last weekend.", "She bought a new dress for the party.", "He forgot his keys at home this morning.", "We spent the afternoon at the beach.", "They decided to take a different route home."],
      B1: ["I spent last summer travelling through several European countries.", "She graduated from university three years ago with a degree in engineering.", "He quit his job last year and started his own small business.", "We moved to a new apartment after our lease expired.", "They cancelled the event due to unexpectedly poor weather conditions."],
      B2: ["I embarked on a solo backpacking trip across Southeast Asia in my early twenties.", "She pursued her passion for photography despite initial resistance from her family.", "He negotiated a significant pay rise after demonstrating consistent high performance.", "We restructured the entire team following the departure of our senior manager.", "They launched the product ahead of schedule, catching competitors completely off guard."],
      C1: ["I undertook an intensive language immersion programme, which fundamentally transformed my approach to communication.", "She championed a major organizational restructuring initiative that significantly improved operational efficiency.", "He navigated a particularly complex legal dispute with remarkable composure and strategic acumen.", "We overhauled our entire supply chain infrastructure in response to unprecedented global disruptions.", "They spearheaded a community-led initiative that successfully revitalized a neglected urban neighbourhood."],
      C2: ["I immersed myself in primary historical sources, which irrevocably altered my understanding of the events in question.", "She orchestrated a diplomatic resolution to what had seemed an intractable institutional conflict.", "He distinguished himself through a series of meticulously executed strategic manoeuvres that redefined the competitive landscape.", "We dismantled entrenched bureaucratic structures that had impeded innovation for decades.", "They confronted the fundamental contradictions inherent in the prevailing paradigm and proposed a coherent alternative framework."],
    }
  },
  {
    keywords: ['present perfect', 'experience', 'have been', 'have done', 'تجربة', 'خبرة'],
    sentences: {
      A1: ["I have eaten pizza before.", "She has visited London.", "We have seen that film.", "He has learned ten words.", "They have played this game."],
      A2: ["I have never been to Japan before.", "She has already finished her homework.", "He has just arrived at the station.", "We have visited three countries this year.", "They have eaten at that restaurant twice."],
      B1: ["I have always wanted to learn how to play the guitar.", "She has recently started taking evening classes in Spanish.", "He has worked in the same company for over a decade.", "We have made significant progress on the project this month.", "They have never experienced such extreme weather conditions before."],
      B2: ["I have accumulated a wealth of experience working across multiple industries over the years.", "She has consistently demonstrated the ability to perform under significant pressure.", "He has successfully completed several challenging projects despite limited resources.", "We have developed a robust framework for managing cross-cultural communication within the team.", "They have maintained an impressive track record of delivering results on time and within budget."],
      C1: ["I have devoted considerable effort to understanding the nuanced interplay between cultural context and linguistic expression.", "She has built an enviable professional reputation through years of meticulous attention to detail.", "He has grappled with some of the most complex ethical dilemmas facing practitioners in his field.", "We have cultivated an environment where intellectual curiosity and rigorous analytical thinking are actively encouraged.", "They have pioneered an approach that seamlessly integrates quantitative analysis with qualitative insight."],
      C2: ["I have spent years interrogating the fundamental assumptions that underpin conventional approaches to this discipline.", "She has forged an intellectual legacy defined by its uncompromising commitment to empirical rigour and conceptual clarity.", "He has traversed the full spectrum of human experience, emerging with a perspective that transcends conventional disciplinary boundaries.", "We have painstakingly assembled a body of evidence that challenges the prevailing orthodoxy with considerable force.", "They have sustained a remarkable trajectory of innovation, consistently anticipating shifts in the landscape before they materialize."],
    }
  },
  {
    keywords: ['future', 'going to', 'will', 'plan', 'مستقبل', 'خطط'],
    sentences: {
      A1: ["I will drink water now.", "She is going to study tonight.", "We will go to the park.", "He will call you later.", "They are going to cook dinner."],
      A2: ["I am going to start a new course next month.", "She will probably arrive before noon.", "He is planning to move to a bigger apartment.", "We will meet at the café at three o'clock.", "They are not going to attend the meeting tomorrow."],
      B1: ["I am planning to take a gap year before starting university.", "She will likely apply for a promotion at the end of this year.", "He is going to renovate his house over the summer break.", "We will probably need to hire additional staff for the upcoming project.", "They are not going to extend the contract under the current terms."],
      B2: ["I intend to pursue postgraduate studies in environmental science in the near future.", "She is set to assume a leadership role within the organization following the restructuring.", "He will inevitably face significant challenges as he transitions into a senior management position.", "We are poised to expand into new markets once the regulatory approvals come through.", "They will need to fundamentally rethink their strategy if they are to remain competitive."],
      C1: ["I am committed to making a meaningful contribution to the field over the course of my career.", "She is preparing to undertake a comprehensive review of the existing policy framework.", "He will be spearheading a major initiative to modernize the organization's digital infrastructure.", "We are positioned to capitalize on emerging opportunities as the market continues to evolve.", "They will inevitably confront the ethical implications of their technological decisions in the years ahead."],
      C2: ["I am resolved to dedicate my professional energies to bridging the gap between theoretical insight and practical application.", "She is poised to reshape the intellectual landscape of her discipline through a series of groundbreaking contributions.", "He will, in all probability, be remembered as one of the defining voices of his generation in this field.", "We are on the cusp of a paradigm shift that will fundamentally redefine our understanding of the subject.", "They will need to contend with the profound structural changes that are already reshaping the global order."],
    }
  },
  {
    keywords: ['travel', 'transport', 'airport', 'journey', 'سفر', 'رحلة', 'مطار'],
    sentences: {
      A1: ["I go by bus to school.", "She takes the train to work.", "We fly to visit family.", "He likes to travel by car.", "They walk to the market."],
      A2: ["I usually book my tickets online a few weeks in advance.", "She prefers to travel by train when the distance is not too great.", "He always packs light when he travels for business.", "We arrived at the airport two hours before departure.", "They took a taxi from the hotel to the city centre."],
      B1: ["I find long-haul flights exhausting, but the destinations always make the journey worthwhile.", "She navigated the busy airport terminal with impressive efficiency.", "He missed his connecting flight due to an unexpected delay at customs.", "We explored the city by bicycle, which turned out to be the best decision of the trip.", "They encountered some difficulties at the border but eventually resolved the situation."],
      B2: ["I prefer off-peak travel to avoid the inevitable crowds and inflated prices.", "She meticulously researched local transport options before arriving in an unfamiliar city.", "He managed to secure a last-minute upgrade by politely inquiring at the check-in desk.", "We traversed the length of the country by rail, which afforded us spectacular views of the landscape.", "They encountered unexpected logistical challenges that tested their ability to adapt under pressure."],
      C1: ["I have developed a highly efficient approach to navigating international travel, minimizing disruption and maximizing productivity.", "She consistently identifies the most cost-effective and environmentally responsible travel options available.", "He handled the bureaucratic complexities of international travel with characteristic patience and resourcefulness.", "We coordinated a complex multi-city itinerary that required meticulous planning across multiple time zones.", "They leveraged their extensive travel experience to advise clients on navigating unfamiliar cultural and logistical terrain."],
      C2: ["I regard travel not merely as physical displacement but as a profound opportunity for cognitive and cultural recalibration.", "She approaches each journey with the intellectual curiosity of an anthropologist and the organizational discipline of a seasoned professional.", "He has traversed some of the world's most challenging terrains, emerging from each experience with heightened resilience and expanded perspective.", "We regard international mobility as an indispensable component of our organizational culture and strategic vision.", "They have cultivated a global network of relationships that would have been inconceivable without their commitment to sustained international engagement."],
    }
  },
  {
    keywords: ['food', 'cooking', 'restaurant', 'eat', 'meal', 'طعام', 'أكل', 'مطعم', 'طبخ'],
    sentences: {
      A1: ["I like pizza very much.", "She cooks rice every day.", "We eat lunch at one o'clock.", "He drinks juice in the morning.", "They love chocolate ice cream."],
      A2: ["I usually cook simple meals at home during the week.", "She ordered a salad and a glass of water at the restaurant.", "He tried sushi for the first time and enjoyed it.", "We always share dessert when we go out for dinner.", "They prefer homemade food to takeaway meals."],
      B1: ["I have developed a passion for cooking authentic dishes from different cuisines.", "She recommended a small restaurant near the market that serves excellent local food.", "He follows a vegetarian diet and always checks the menu carefully before ordering.", "We decided to try a cooking class together during our holiday.", "They spent the entire afternoon preparing a traditional meal for the family gathering."],
      B2: ["I find that cooking from scratch is both more economical and more rewarding than relying on convenience food.", "She has a remarkable talent for improvising delicious meals from whatever ingredients happen to be available.", "He studied the local culinary traditions in considerable depth before writing his food blog.", "We sourced all our ingredients from local farmers, which significantly enhanced the quality of the final dish.", "They transformed a straightforward recipe into an extraordinary dining experience through careful attention to presentation and flavour."],
      C1: ["I believe that culinary traditions serve as a powerful lens through which to examine the cultural values and historical experiences of a society.", "She approaches food photography with the same meticulous attention to composition and lighting that characterizes her broader artistic practice.", "He has developed an encyclopedic knowledge of global cuisines, which he attributes to years of deliberate exploration and open-minded experimentation.", "We designed the menu to reflect the rich diversity of regional culinary traditions, ensuring that each dish told a compelling story.", "They have successfully repositioned their restaurant as a destination for discerning diners seeking authentic, sustainably sourced cuisine."],
      C2: ["I regard the act of cooking as a form of applied philosophy — a daily negotiation between tradition and innovation, constraint and creativity.", "She has articulated a compelling vision for a culinary culture that prioritizes ecological sustainability without compromising gastronomic excellence.", "He occupies a unique position at the intersection of anthropology and gastronomy, using food as a vehicle for exploring the deepest questions of human identity.", "We have cultivated a supply chain that is as ethically rigorous as it is logistically sophisticated, setting a new benchmark for the industry.", "They have challenged the conventional wisdom surrounding fine dining, demonstrating that accessibility and excellence need not be mutually exclusive."],
    }
  },
  {
    keywords: ['work', 'job', 'career', 'office', 'business', 'عمل', 'وظيفة', 'مهنة'],
    sentences: {
      A1: ["I work in an office.", "She is a doctor.", "We start work at eight.", "He teaches English at school.", "They work from home today."],
      A2: ["I applied for a new job last week.", "She works as a nurse at the local hospital.", "He has a meeting with his manager every Monday.", "We finish work at five o'clock on Fridays.", "They are looking for experienced candidates to join the team."],
      B1: ["I am considering a career change after spending five years in the same role.", "She manages a small team of five people and finds it both challenging and rewarding.", "He was promoted to senior manager after demonstrating exceptional leadership skills.", "We collaborated with colleagues from three different countries on this project.", "They are currently recruiting for a number of positions across various departments."],
      B2: ["I have deliberately cultivated a diverse professional skill set to remain adaptable in a rapidly changing job market.", "She negotiated a flexible working arrangement that allows her to balance her professional and personal responsibilities more effectively.", "He demonstrated exceptional crisis management skills when the project encountered significant unforeseen obstacles.", "We implemented a comprehensive staff development programme that has measurably improved team performance.", "They restructured the entire department to improve efficiency and reduce unnecessary duplication of effort."],
      C1: ["I have consistently advocated for a more humane and sustainable approach to organizational management.", "She has distinguished herself as a transformational leader who inspires those around her to perform at the highest level.", "He navigated the complex political dynamics of a large multinational organization with considerable skill and diplomacy.", "We established a culture of psychological safety that enabled team members to share ideas without fear of judgment or reprisal.", "They pioneered a hybrid working model that has since been adopted as best practice across the wider industry."],
      C2: ["I have dedicated my career to challenging the prevailing assumptions that underpin conventional approaches to organizational leadership.", "She embodies the rare combination of visionary thinking and operational precision that distinguishes truly exceptional leaders.", "He has carved out a distinctive intellectual niche at the confluence of organizational behaviour, ethics, and strategic management.", "We have constructed an organizational culture that values intellectual courage, ethical integrity, and collaborative innovation in equal measure.", "They have redefined the boundaries of what is achievable within a constrained resource environment through visionary leadership and rigorous execution."],
    }
  },
  {
    keywords: ['conditional', 'if', 'unless', 'wish', 'شرطي', 'لو', 'إذا'],
    sentences: {
      A1: ["If I am tired, I sleep early.", "She drinks water if she is thirsty.", "We stay home if it rains.", "He calls me if he needs help.", "If you are hungry, eat something."],
      A2: ["If I have free time tomorrow, I will visit my friend.", "She will be happy if she passes her exam.", "He will call you if he arrives late.", "We will cancel the trip if the weather is bad.", "If you study hard, you will improve your English."],
      B1: ["If I had more time, I would learn a musical instrument.", "She would travel more often if flights were not so expensive.", "He would apply for the position if he felt more confident about his qualifications.", "We would have finished earlier if we had started on time.", "If they had listened more carefully, they would have avoided the misunderstanding."],
      B2: ["If I were in a position to make that decision, I would prioritize long-term sustainability over short-term profit.", "She would have handled the situation differently had she been fully aware of all the relevant facts.", "He might have pursued a different career path had circumstances been more favourable early in his life.", "We would not have encountered these difficulties had we conducted a more thorough risk assessment at the outset.", "If they had invested more heavily in staff development, they would not be facing such significant retention challenges now."],
      C1: ["Were I to reconsider my approach, I would place considerably greater emphasis on stakeholder engagement from the very beginning.", "Had she anticipated the scale of the disruption, she would have put far more robust contingency plans in place.", "Should the situation deteriorate further, we will have no alternative but to seek external mediation.", "Had we secured adequate funding at an earlier stage, the project would have been completed well within the original timeframe.", "Were they to adopt a more collaborative approach, the prospects for a mutually satisfactory resolution would improve considerably."],
      C2: ["Were one to examine the counterfactual with the benefit of hindsight, it becomes apparent that a fundamentally different strategic approach was both available and preferable.", "Had the organization possessed the foresight to anticipate these structural shifts, it might have positioned itself considerably more advantageously.", "Should the proposed reforms fail to achieve the intended outcomes, the consequences for institutional credibility would be severe and potentially irreversible.", "Had a more rigorous evidence base been established prior to the intervention, many of the subsequent difficulties could have been anticipated and mitigated.", "Were the fundamental assumptions underlying the current framework subjected to more sustained critical scrutiny, their inherent fragility would become immediately apparent."],
    }
  },
]

// Default level sentences when no topic matches
const SHADOWING_DEFAULT = {
  A1: ["Hello! My name is Sara.", "I go to school every day.", "She has a cat and a dog.", "We eat breakfast together.", "The sun is bright today."],
  A2: ["I enjoy spending time with my family on weekends.", "She usually takes the bus to work in the morning.", "He is learning English because he wants to travel.", "We often have lunch at the small café near the office.", "They like going for a walk after dinner every evening."],
  B1: ["I have been studying English for about three years now.", "She decided to apply for a new job after feeling bored with her current one.", "He finds it helpful to read English articles every morning before starting work.", "We managed to finish the project on time despite having very limited resources.", "They moved to a new city last year and are slowly getting used to the lifestyle."],
  B2: ["I have noticed significant improvements in my fluency since I started speaking English every day.", "She believes that consistent practice, rather than occasional intensive study, is the key to language acquisition.", "He approached the negotiation with a clear strategy and managed to secure highly favourable terms.", "We restructured our daily schedule to allow for more focused, uninterrupted periods of deep work.", "They collaborated effectively across different time zones by establishing clear communication protocols from the start."],
  C1: ["I am convinced that the most effective language learners approach the process with a combination of discipline, curiosity, and genuine intellectual engagement.", "She has developed a remarkably nuanced understanding of the cultural subtext embedded in everyday English conversation.", "He consistently demonstrates the ability to articulate complex ideas with both precision and elegance across a variety of contexts.", "We have established a shared understanding that excellence requires not merely technical competence but also creative thinking and interpersonal sensitivity.", "They have cultivated a learning environment in which ambiguity is embraced rather than avoided, fostering deeper cognitive engagement."],
  C2: ["I regard linguistic mastery not as a destination to be reached but as an ongoing process of refinement, requiring sustained intellectual humility.", "She navigates the subtle gradations of register, tone, and pragmatic implication with the assurance of a native speaker of long standing.", "He constructs his arguments with a clarity and economy of expression that belies the considerable complexity of the ideas he is articulating.", "We have arrived at a shared understanding that the highest form of communication transcends the mere transmission of information to encompass the cultivation of genuine understanding.", "They engage with the language at a level that reveals not merely proficiency but a deep internalization of the cultural and intellectual traditions from which it emerges."],
}

const LEVEL_CEFR = { '1': 'A1', '2': 'A2', '3': 'B1', '4': 'B2', '5': 'C1', '6': 'C2' }
const CEFR_VALID = new Set(['A1','A2','B1','B2','C1','C2'])

function getShadowingSentences(dayTitle, levelId) {
  const s = String(levelId)
  const cefr = CEFR_VALID.has(s) ? s : (LEVEL_CEFR[s] || 'A1')
  const title = (dayTitle || '').toLowerCase()
  for (const topic of SHADOWING_TOPICS) {
    if (topic.keywords.some(k => title.includes(k.toLowerCase()))) {
      return topic.sentences[cefr] || SHADOWING_DEFAULT[cefr]
    }
  }
  return SHADOWING_DEFAULT[cefr]
}

/* ─── Shadowing ─── */
function ShadowingComp({ day, levelId }) {
  const { shadowing: s } = day
  const [playingIdx, setPlayingIdx] = useState(null)
  const [shadLang, setShadLang] = useState('en-US')
  const [recording, setRecording] = useState(null)
  const [audioURLs, setAudioURLs] = useState({})
  const [scores, setScores] = useState({})   // { idx: { heard, pct, label, color } }
  const [scoring, setScoring] = useState({}) // { idx: true } while transcription is in flight
  const mediaRecorderRef = useRef(null)
  const chunksRef = useRef([])
  const sentences = (s.sentences && s.sentences.length >= 5)
    ? s.sentences.slice(0, 5)
    : getShadowingSentences(day.title, levelId)

  const playSentence = (text, idx) => {
    if (playingIdx === idx) { stopTTS(); setPlayingIdx(null); return }
    setPlayingIdx(idx)
    speakAtRate(text, 0.82, shadLang, () => setPlayingIdx(null))
  }

  const evalPronunciation = (heard, target) => {
    const h = heard.toLowerCase().replace(/[^a-z ]/g, '').trim()
    const t = target.toLowerCase().replace(/[^a-z ]/g, '').trim()
    if (!h) return null
    const tWords = t.split(' ')
    const heardWords = h.split(/\s+/)
    const matched = tWords.filter(w => heardWords.includes(w)).length
    const pct = Math.round((matched / tWords.length) * 100)
    if (pct === 100) return { heard, pct, label: '✅ ممتاز! النطق مثالي', color: '#22c55e' }
    if (pct >= 70)   return { heard, pct, label: `👍 جيد جداً — ${pct}%`, color: '#84cc16' }
    if (pct >= 40)   return { heard, pct, label: `🔄 قريب — ${pct}%، حاول مجدداً`, color: '#f59e0b' }
    return               { heard, pct, label: `💪 استمع مجدداً — ${pct}%`, color: '#ef4444' }
  }

  const scoreRecording = async (idx, blob) => {
    setScoring(prev => ({ ...prev, [idx]: true }))
    try {
      const form = new FormData()
      form.append('audio', blob, 'recording.webm')
      const res = await fetch(`${BACKEND}/english-tutor/transcribe`, { method: 'POST', body: form })
      const d = await res.json()
      if (res.ok && d.transcript && d.transcript !== '[inaudible]') {
        const result = evalPronunciation(d.transcript, sentences[idx])
        if (result) setScores(prev => ({ ...prev, [idx]: result }))
      } else if (res.ok) {
        setScores(prev => ({ ...prev, [idx]: { heard: '', pct: 0, label: '🎤 ما سمعنا كلام واضح — جرّبي مرة ثانية', color: '#ef4444' } }))
      }
    } catch { /* transcription is best-effort — recording itself already succeeded */ }
    setScoring(prev => { const n = { ...prev }; delete n[idx]; return n })
  }

  const startRecord = async (idx) => {
    if (recording === idx) {
      mediaRecorderRef.current?.stop()
      return
    }
    // Clear previous score for this sentence
    setScores(prev => { const n = { ...prev }; delete n[idx]; return n })

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      chunksRef.current = []
      const mr = new MediaRecorder(stream)
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        const url = URL.createObjectURL(blob)
        setAudioURLs(prev => ({ ...prev, [idx]: url }))
        stream.getTracks().forEach(t => t.stop())
        setRecording(null)
        track('shadowing_recorded')
        scoreRecording(idx, blob)
      }
      mediaRecorderRef.current = mr
      setRecording(idx)
      mr.start()
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        alert('لم يُمنح إذن الميكروفون — اسمحي للموقع باستخدام الميكروفون من إعدادات المتصفح/الجهاز 🎤')
      } else if (err?.name === 'NotFoundError') {
        alert('ما لقينا ميكروفون على هذا الجهاز')
      } else {
        alert('تعذّر تشغيل التسجيل — جرّبي مرة ثانية')
      }
    }
  }

  return (
    <div className="el-section">
      <div className="el-shadowing-chunk-box">
        <div className="el-chunk-label">القالب اللغوي اليوم</div>
        <div className="el-chunk-text">"{s.chunk}"</div>
        <DualSpeak text={s.chunk} style={{ fontSize: '1.1rem', margin: '8px auto', justifyContent: 'center' }} />
        <div className="el-chunk-native">
          <span className="el-native-label">النطق الأصيل:</span>
          <span className="el-native-form">{s.nativeForm}</span>
        </div>
      </div>

      <div className="el-shadowing-explain">{s.explanation}</div>

      <div className="el-steps-title">🎯 روتين الشادونج — 3 خطوات</div>
      <div className="el-steps-list">
        {s.steps.map((step, i) => (
          <div key={i} className="el-step-item">
            <div className="el-step-num">{i + 1}</div>
            <div className="el-step-text">{step}</div>
          </div>
        ))}
      </div>

      <div className="el-shadow-sentences-title">🗣️ تدرّب على هذه الجمل الخمس — كرّر خلف الصوت</div>
      <div style={{ display: 'flex', gap: 6, margin: '6px 0 2px' }}>
        <button className={`el-speed-ctrl-btn${shadLang === 'en-US' ? ' active' : ''}`} onClick={() => setShadLang('en-US')}>🇺🇸 US</button>
        <button className={`el-speed-ctrl-btn${shadLang === 'en-GB' ? ' active' : ''}`} onClick={() => setShadLang('en-GB')}>🇬🇧 UK</button>
      </div>
      <div className="el-shadow-sentences-desc">استمع → كرّر → سجّل صوتك → اسمع تسجيلك</div>
      <div className="el-shadow-sentences-list">
        {sentences.map((sent, i) => (
          <div key={i} className="el-shadow-sentence-card">
            <div className="el-shadow-sent-num">{i + 1}</div>
            <div className="el-shadow-sent-body">
              <div className="el-shadow-sent-text">{sent}</div>
              <div className="el-shadow-sent-btns">
                <button
                  className={`el-shadow-play-btn${playingIdx === i ? ' playing' : ''}`}
                  onClick={() => playSentence(sent, i)}
                >
                  {playingIdx === i ? '⏹ أوقف' : '🔊 استمع'}
                </button>
                <button
                  className={`el-shadow-rec-btn${recording === i ? ' recording' : ''}`}
                  onClick={() => startRecord(i)}
                >
                  {recording === i ? '⏹ أوقف' : '🎤 سجّل'}
                </button>
              </div>
              {audioURLs[i] && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '.78rem', color: 'var(--el-accent)', fontWeight: 600 }}>🔁 تسجيلك:</span>
                    <audio src={audioURLs[i]} controls style={{ height: 28, flex: 1, minWidth: 160 }} />
                    <button
                      className="el-nav-btn"
                      style={{ fontSize: '.72rem', padding: '2px 8px' }}
                      onClick={() => {
                        setAudioURLs(prev => { const n = { ...prev }; delete n[i]; return n })
                        setScores(prev => { const n = { ...prev }; delete n[i]; return n })
                        setScoring(prev => { const n = { ...prev }; delete n[i]; return n })
                      }}
                    >🗑</button>
                  </div>
                  {scoring[i] && (
                    <div style={{ padding: '6px 12px', borderRadius: 8, fontSize: '.83rem', color: 'var(--el-muted)' }}>
                      ⏳ جاري تحليل النطق...
                    </div>
                  )}
                  {scores[i] && !scoring[i] && (
                    <div style={{
                      padding: '6px 12px', borderRadius: 8, fontSize: '.83rem', fontWeight: 600,
                      background: scores[i].color + '22', color: scores[i].color,
                      border: `1px solid ${scores[i].color}55`
                    }}>
                      {scores[i].label}
                      {scores[i].pct < 100 && scores[i].heard && (
                        <div style={{ fontSize: '.75rem', fontWeight: 400, marginTop: 2, opacity: .85 }}>
                          سمعنا: "{scores[i].heard}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {(() => {
        const ytUrl = s.youtubeUrl ||
          `https://www.youtube.com/results?search_query=${encodeURIComponent((s.chunk || '') + ' english pronunciation practice shadowing')}`
        return (
          <a href={ytUrl} target="_blank" rel="noopener noreferrer" className="el-yt-btn">
            🔍 ابحث على YouTube — تدرّب مع متحدث أصيل
          </a>
        )
      })()}

      <DialoguePartner day={day} />
    </div>
  )
}

/* ─── Live Correction Parser ─── */
const NO_ERRORS_MARK = 'NO_ERRORS_FOUND'

function parseCorrectionResponse(text) {
  // Parse lines like: CORRECTION: [original] → [fixed] | Reason: [why]
  // Tolerant of numbering/bullets, "->" instead of "→", and ":"/"-" instead of "|".
  const lines = text.split('\n')
  const corrections = []
  for (const line of lines) {
    const cleaned = line.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '')
    const match = cleaned.match(/CORRECTION:\s*(.+?)\s*(?:→|->)\s*(.+?)(?:\s*(?:\||-)\s*Reason:\s*(.+))?$/i)
    if (match) {
      const original = match[1].trim(), fixed = match[2].trim()
      if (original === fixed) continue // model sometimes lists a word it did not change
      corrections.push({ original, fixed, reason: match[3]?.trim() || '' })
    }
  }
  return corrections
}

// True only when the model explicitly emitted the NO_ERRORS_FOUND sentinel
// from the system prompt — never inferred just from a parse miss, since a
// reply that doesn't match our strict CORRECTION: format is NOT proof the
// text was error-free. A plain-language token (not an emoji) is used because
// emoji/checkmarks can appear incidentally in a friendly reply unrelated to
// whether the text was actually error-free.
function isExplicitNoErrors(text) {
  return text.toUpperCase().includes(NO_ERRORS_MARK)
}

// Client-side safety net, independent of the AI: catches obvious keyboard-mash
// gibberish ("htjfdnxhndhnn asvgaz sb s") before even calling the model, so a
// bad/uncooperative AI reply can never claim gibberish is "perfect English."
function looksLikeGibberish(text) {
  const words = text.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return false
  const implausible = words.filter(w => {
    const letters = w.replace(/[^a-zA-Z']/g, '')
    if (letters.length <= 2) return false // short real words (a, I, to, is...) are fine
    return !/[aeiouAEIOU]/.test(letters) || letters.length > 14
  })
  return implausible.length / words.length > 0.3
}

/* ─── Inline Correction Display ─── */
function CorrectionDisplay({ corrections, originalText }) {
  if (!corrections.length) return null
  // Find each correction once in the student's text (whole words only, no
  // overlaps) instead of replacing every occurrence — replacing "i" everywhere
  // used to hit the "i" inside "is" and even inside the markup itself.
  const isWordCh = ch => ch !== undefined && /[a-z0-9']/i.test(ch)
  const lower = originalText.toLowerCase()
  const ranges = []
  for (const c of corrections) {
    const needle = c.original.toLowerCase()
    if (!needle) continue
    const startsW = isWordCh(needle[0]), endsW = isWordCh(needle[needle.length - 1])
    let from = 0, idx
    while ((idx = lower.indexOf(needle, from)) !== -1) {
      const end = idx + needle.length
      const clashes = ranges.some(r => idx < r.end && end > r.start)
      if (!clashes && !(startsW && isWordCh(lower[idx - 1])) && !(endsW && isWordCh(lower[end]))) {
        ranges.push({ start: idx, end, c })
        break
      }
      from = idx + 1
    }
  }
  ranges.sort((x, y) => x.start - y.start)
  const parts = []
  let pos = 0
  ranges.forEach((r, i) => {
    if (r.start > pos) parts.push(<span key={`t${i}`}>{originalText.slice(pos, r.start)}</span>)
    parts.push(<del key={`d${i}`} className="el-correction-del">{originalText.slice(r.start, r.end)}</del>)
    parts.push(<span key={`s${i}`}> </span>)
    parts.push(<ins key={`i${i}`} className="el-correction-ins">{r.c.fixed}</ins>)
    pos = r.end
  })
  if (pos < originalText.length) parts.push(<span key="tail">{originalText.slice(pos)}</span>)
  return (
    <div className="el-correction-display">
      <div className="el-correction-label">✏️ التصحيح بالخط الأحمر</div>
      <div className="el-correction-text" dir="ltr">{parts}</div>
      {corrections.map((c, i) => (
        <div key={i} className="el-correction-note">
          <span dir="ltr" style={{ fontWeight: 600 }}>{c.original} → {c.fixed}</span>
          <div><span className="el-correction-why">💡 لماذا؟</span> {c.reason}</div>
        </div>
      ))}
    </div>
  )
}

/* ─── Writing & AI Chat ─── */
function WritingComp({ day, levelId, dayId, navigate, setBuddyMessages, setBuddyInput, setAvatarState, progress }) {
  const draftBase = `${levelId}-${dayId}-writing`

  // load saved drafts on mount
  const [responses, setResponses] = useState(() => {
    const saved = {}
    day.writing?.challenges?.forEach((_, i) => {
      const v = progress.loadDraft(`${draftBase}-${i}`)
      if (v) saved[i] = v
    })
    return saved
  })
  const [corrections, setCorrections] = useState({})
  const [checking, setChecking] = useState({})
  const anyCheckingRef = useRef(false) // H-10: global lock — prevent 6 concurrent AI calls
  const [savedAt, setSavedAt] = useState({})
  const debounceRefs = useRef({})
  const { writing: w } = day

  const handleWritingChange = (i, value) => {
    setResponses(r => ({ ...r, [i]: value }))
    setCorrections(c => ({ ...c, [i]: null }))
    // debounce: save after 3s of inactivity
    clearTimeout(debounceRefs.current[i])
    debounceRefs.current[i] = setTimeout(() => {
      progress.saveDraft(`${draftBase}-${i}`, value)
      setSavedAt(s => ({ ...s, [i]: new Date().toLocaleTimeString('ar') }))
    }, 3000)
  }

  const sendToBuddy = (text, challengeIdx) => {
    if (!text.trim()) return
    const msg = `تحدي الكتابة ${challengeIdx + 1}:\n${w.challenges[challengeIdx]}\n\nإجابتي:\n${text}`
    setBuddyInput('')
    setBuddyMessages(prev => [...prev, { role: 'user', content: msg }])
  }

  const checkLive = async (text, idx) => {
    if (!text.trim() || checking[idx] || anyCheckingRef.current) return
    if (looksLikeGibberish(text)) {
      setCorrections(c => ({
        ...c,
        [idx]: {
          raw: `CORRECTION: ${text} → [اكتب جملة إنجليزية حقيقية] | Reason: هذا مش كلام إنجليزي مفهوم`,
          parsed: [{ original: text, fixed: '[اكتب جملة إنجليزية حقيقية]', reason: 'هذا مش كلام إنجليزي مفهوم — أعيدي كتابة الجملة' }],
        },
      }))
      return
    }
    anyCheckingRef.current = true
    setChecking(c => ({ ...c, [idx]: true }))
    setAvatarState('correcting')
    try {
      const systemPrompt = `أنت مُصحِّح لغوي دقيق صارم. اقرأ نص الطالب بعناية وابحث عن كل خطأ نحوي أو إملائي أو في اختيار الكلمة، مهما كان بسيطاً (تصريف الفعل، أدوات التعريف، حروف الجر، تركيب الجملة، الإملاء). لا تتساهل أبداً — أي جملة غير صحيحة نحوياً بالإنجليزية القياسية تُعتبر خطأ ويجب تصحيحه.

إذا كان النص عبارة عن حروف عشوائية أو طرق لوحة مفاتيح عشوائية أو ليس كلمات إنجليزية حقيقية على الإطلاق (مثل "htjfdnxhndhnn asvgaz sb s")، فهذا بحد ذاته خطأ فادح — لا تقل أبداً إنه لا يوجد أخطاء. بدلاً من ذلك اكتب سطر تصحيح واحد يغطي النص كاملاً:
CORRECTION: [النص كاملاً] → [اطلب من الطالب إعادة الكتابة بجمل إنجليزية حقيقية] | Reason: هذا مش كلام إنجليزي مفهوم، لازم تكتب جملة حقيقية

انتبه بشكل خاص لتطابق الفاعل مع الفعل (subject-verb agreement) — هذا أكثر خطأ يهمله المصححون الكسولون. أمثلة على أخطاء يجب أن تُمسك دائماً:
- "I is happy" ← خطأ، الصح "I am happy" (مع I نستخدم am وليس is)
- "She go to school" ← خطأ، الصح "She goes to school" (مع he/she/it نضيف s للفعل بصيغة المضارع البسيط)
- "They is" ← خطأ، الصح "They are"
- "He have" ← خطأ، الصح "He has"
تحقق من كل جملة على حدة: هل الفاعل مفرد أم جمع؟ هل الفعل المساعد (am/is/are/was/were/have/has) يطابقه؟ لا تكتفِ بتصحيح الإملاء والأحرف الكبيرة وتتجاهل تطابق الفاعل والفعل.

أعد الرد بهذا التنسيق الصارم فقط، بدون أي مقدمة أو نص إضافي:

CORRECTION: [الكلمة/العبارة الخاطئة] → [الصواب] | Reason: [شرح قصير بالعربي]

اكتب سطراً واحداً لكل خطأ — راجع الجملة مرتين قبل أن تقرر أنها خالية من الأخطاء. إذا وفقط إذا كان النص جملة إنجليزية حقيقية ومفهومة وخالية تماماً من أي خطأ (بما فيها تطابق الفاعل والفعل)، اكتب هذا السطر فقط: ${NO_ERRORS_MARK}`
      // The tutor endpoint treats the message as chat unless the task is explicit,
      // so wrap the student's text in a clear correction request.
      const task = `صحّح نص الطالب التالي فقط، ولا تجب عليه ولا تحادثه. أعد الرد بأسطر CORRECTION: فقط، أو ${NO_ERRORS_MARK} إذا لا أخطاء.

مثال على الشكل المطلوب:
CORRECTION: She go → She goes | Reason: مع she نضيف es للفعل

نص الطالب:
"""
${text}
"""`
      const reply = await aiAsk(task, systemPrompt)
      const parsed = parseCorrectionResponse(reply)
      setCorrections(c => ({ ...c, [idx]: { raw: reply, parsed } }))
      setAvatarState(parsed.length ? 'correcting' : 'happy')
      setBuddyMessages(prev => [...prev, { role: 'assistant', content: reply }])
    } catch {
      setAvatarState('idle')
    } finally {
      anyCheckingRef.current = false
      setChecking(c => ({ ...c, [idx]: false }))
      setTimeout(() => setAvatarState('idle'), 3000)
    }
  }

  return (
    <div className="el-section">
      <WritingPromptCard dayTitle={day.title} />
      <div className="el-writing-title">✍️ تحديات الكتابة</div>
      {w.challenges.map((ch, i) => (
        <div key={i} className="el-writing-challenge">
          <div className="el-challenge-q">{i + 1}. {ch}</div>
          <textarea
            className="el-writing-area"
            placeholder="اكتب إجابتك هنا بالإنجليزية..."
            value={responses[i] || ''}
            onChange={e => handleWritingChange(i, e.target.value)}
            rows={5}
          />
          <div className="el-draft-status">
            {savedAt[i]
              ? `💾 حُفظ تلقائياً ${savedAt[i]}`
              : responses[i] ? '⏳ سيُحفظ خلال 3 ثوانٍ...' : ''}
          </div>
          <div className="el-writing-btns">
            <button
              className="el-nav-btn el-check-btn"
              onClick={() => checkLive(responses[i] || '', i)}
              disabled={!responses[i]?.trim() || checking[i]}
            >
              {checking[i] ? '⏳ جاري التصحيح...' : '🖊️ صحّح فوراً'}
            </button>
            <button
              className="el-nav-btn"
              onClick={() => sendToBuddy(responses[i] || '', i)}
              disabled={!responses[i]?.trim()}
            >
              🤖 ناقش مع المساعد
            </button>
          </div>
          {corrections[i] && (
            corrections[i].parsed.length > 0
              ? <CorrectionDisplay corrections={corrections[i].parsed} originalText={responses[i] || ''} />
              : isExplicitNoErrors(corrections[i].raw)
                ? <div className="el-correction-ok">✅ ممتاز! لا أخطاء في هذه الفقرة.</div>
                : <div className="el-correction-display"><div className="el-correction-text">{corrections[i].raw}</div></div>
          )}
          {corrections[i] && <WritingUpgrade text={responses[i] || ''} />}
        </div>
      ))}
    </div>
  )
}

/* ─── Grammar Detective ─── */

/* Build lesson-specific exercises from the day's grammar patterns */
function buildLessonBank(day) {
  const items = []
  if (!day?.grammar?.patterns) return items

  day.grammar.patterns.forEach(pattern => {
    // 1. Correction exercises ("صحّح" / "Correct")
    pattern.exercises?.forEach(ex => {
      if (!/صحّح|correct|Correct/.test(ex.question)) return
      const quoted = ex.question.match(/[''""]([^''""\n]{4,80})[''""]/)
      if (!quoted) return
      const wrongSentence = quoted[1].trim()
      // Pull hint from parentheses in the answer
      const hintMatch = ex.answer.match(/\(([^)]{5,})\)/)
      const hint = hintMatch ? hintMatch[1] : (pattern.explanation || pattern.name).split('.')[0]
      // Extract correct text (before any parenthesis)
      const correctText = ex.answer.replace(/\(.*?\)/g, '').replace(/\.$/, '').trim()
      // Diff wrong vs correct to find the error word(s)
      const wWords = wrongSentence.replace(/[.!?]$/, '').split(/\s+/)
      const cWords = correctText.split(/\s+/)
      const errArr = [], fixArr = []
      wWords.forEach((w, i) => {
        const wc = w.toLowerCase().replace(/[.,!?'"]/g, '')
        const cc = (cWords[i] || '').toLowerCase().replace(/[.,!?'"]/g, '')
        if (wc !== cc) { errArr.push(w.replace(/[.,!?'"]/g, '')); fixArr.push(cWords[i]?.replace(/[.,!?'"]/g, '') || '') }
      })
      if (errArr.length === 0 || errArr.length > 4) return
      items.push({ text: wrongSentence, error: errArr.join(' '), fix: fixArr.join(' '), hint, lessonSpecific: true })
    })

    // 2. Examples as correct sentences
    pattern.examples?.forEach(example => {
      items.push({
        text: example,
        error: 'none',
        fix: 'Correct!',
        hint: `جملة صحيحة — ${pattern.name}`,
        lessonSpecific: true,
      })
    })
  })
  return items
}

function GrammarDetective({ day, levelId, dayId }) {
  const [open, setOpen] = useState(false)
  const [sentences, setSentences] = useState([])
  const [clicked, setClicked] = useState({})
  const [revealed, setRevealed] = useState({})
  const [attempted, setAttempted] = useState({})
  const [generated, setGenerated] = useState(false)
  const [usingLesson, setUsingLesson] = useState(false)

  const generate = () => {
    track('grammar_detective_used')
    const authored = getDetectiveSentences(levelId, dayId)
    if (authored && authored.length > 0) {
      setSentences(authored)
      setUsingLesson(true)
    } else {
      // Content for this day hasn't been hand-authored yet — fall back to
      // whatever this lesson's own exercises/examples provide, never to
      // unrelated static content from another grammar topic.
      const lessonBank = buildLessonBank(day)
      setSentences(lessonBank.slice(0, 5))
      setUsingLesson(lessonBank.length > 0)
    }
    setClicked({})
    setRevealed({})
    setAttempted({})
    setGenerated(true)
  }

  const handleWord = (sIdx, wIdx, word, sentence) => {
    const key = `${sIdx}_${wIdx}`
    if (clicked[key]) return
    setAttempted(a => ({ ...a, [sIdx]: true }))
    if (sentence.error === 'none') { setClicked(c => ({ ...c, [key]: 'wrong' })); return }
    const cleanWord = word.toLowerCase().replace(/[.,!?'"]/g, '')
    const errorWords = sentence.error.toLowerCase().split(/\s+/).map(w => w.replace(/[.,!?'"]/g, ''))
    const isError = errorWords.some(ew => cleanWord === ew || (ew.length > 3 && cleanWord.includes(ew)) || (cleanWord.length > 3 && ew.includes(cleanWord)))
    setClicked(c => ({ ...c, [key]: isError ? 'right' : 'wrong' }))
  }

  return (
    <div className="el-detective-section" style={{ marginTop: 20 }}>
      <button className="el-roleplay-toggle" style={{ marginBottom: open ? 14 : 0 }} onClick={() => {
        const next = !open
        setOpen(next)
        if (next && !generated) generate()
      }}>
        🔍 {open ? 'إغلاق Grammar Detective' : 'Grammar Detective — اكتشف الأخطاء المخفية'}
      </button>
      {open && (
        <>
          {generated && sentences.length > 0 && (
            <>
              <div className="el-detective-desc">
                {usingLesson ? '📚 الجمل مرتبطة بدرس اليوم — ' : ''}
                اضغط على الكلمة الخاطئة في كل جملة (بعض الجمل قد تكون صحيحة!):
              </div>
              {sentences.map((s, sIdx) => {
                const words = s.text.split(' ')
                const hasAttempted = !!attempted[sIdx]
                const clickedCorrectBtn = !!clicked[`${sIdx}_correct`]
                const clickedErrorWord = Object.entries(clicked).some(([k, v]) => k.startsWith(`${sIdx}_`) && v === 'right')
                const foundAll = s.error === 'none' ? clickedCorrectBtn : clickedErrorWord
                const wrongClick = s.error === 'none' && hasAttempted && !clickedCorrectBtn
                const allFixes = s.fixes || (s.fix ? [s.fix] : [])
                const fixDisplay = allFixes.length > 1
                  ? `اجابات مقبولة: ${allFixes.join(' أو ')}`
                  : `الصواب: ${s.fix}`
                return (
                  <div key={sIdx} className="el-detective-sentence">
                    {s.lessonSpecific && (
                      <div style={{ fontSize: '.72rem', color: 'var(--el-accent)', marginBottom: 3, fontWeight: 600 }}>📖 من درس اليوم</div>
                    )}
                    <div className="el-detective-text">
                      {words.map((w, wIdx) => {
                        const key = `${sIdx}_${wIdx}`
                        const st = clicked[key]
                        return (
                          <span key={wIdx}>
                            <span
                              className={`el-detective-word${st === 'right' ? ' clicked-right' : st === 'wrong' ? ' clicked-wrong' : ''}`}
                              onClick={() => handleWord(sIdx, wIdx, w, s)}
                            >{w}</span>{' '}
                          </span>
                        )
                      })}
                    </div>
                    {/* Show "correct sentence" button only after user has tried clicking a word */}
                    {s.error === 'none' && hasAttempted && !foundAll && !revealed[sIdx] && (
                      <button
                        className="el-nav-btn"
                        style={{ marginTop: 8, padding: '4px 10px', fontSize: '.78rem', background: '#f0fdf4', borderColor: '#86efac', color: '#15803d' }}
                        onClick={() => setClicked(c => ({ ...c, [`${sIdx}_correct`]: 'right' }))}
                      >
                        ✅ هذه الجملة صحيحة (لا خطأ)
                      </button>
                    )}
                    {wrongClick && !foundAll && !revealed[sIdx] && (
                      <div style={{ fontSize: '.78rem', color: '#f59e0b', marginTop: 4 }}>
                        ⚠️ انتبه — هل تعتقد أن الجملة خاطئة؟ ابحث أكثر، أو اضغط "أظهر الإجابة"
                      </div>
                    )}
                    {foundAll ? (
                      <div className="el-detective-result found">
                        {s.error === 'none'
                          ? `✅ صحيح! هذه الجملة صحيحة — ${s.hint}`
                          : `✅ الخطأ: ${s.error} ← ${fixDisplay} — ${s.hint}`}
                      </div>
                    ) : hasAttempted && !revealed[sIdx] && s.error !== 'none' && (
                      <button
                        className="el-nav-btn"
                        style={{ marginTop: 8, padding: '4px 12px', fontSize: '.78rem' }}
                        onClick={() => setRevealed(r => ({ ...r, [sIdx]: true }))}
                      >
                        💡 أظهر الإجابة
                      </button>
                    )}
                    {revealed[sIdx] && !foundAll && (
                      <div className="el-detective-result">
                        الخطأ: <strong>{s.error}</strong> ← <strong>{fixDisplay}</strong> — {s.hint}
                        {allFixes.length > 1 && (
                          <div style={{ marginTop: 4, fontSize: '.8rem', opacity: .85 }}>
                            💡 أكثر من إجابة صحيحة مقبولة
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </>
      )}
    </div>
  )
}


/* ─── Writing Upgrade (3 levels) ─── */
function WritingUpgrade({ text }) {
  const [level, setLevel] = useState(null)
  const [result, setResult] = useState({})
  const [loading, setLoading] = useState(false)

  const upgrade = async (lvl) => {
    setLevel(lvl)
    if (result[lvl]) return
    setLoading(true)
    try {
      const prompts = {
        b1: 'Improve this text to B1 level: clearer vocabulary, simple improvements. Show ONLY the improved text.',
        b2: 'Rewrite this text at B2 level: professional vocabulary, varied sentence structure. Show ONLY the improved text.',
        c2: 'Rewrite this text at C2/native level: sophisticated, academic, polished. Show ONLY the improved text.'
      }
      const upgraded = await aiAsk(`${prompts[lvl]}\n\nText: "${text}"`, 'You are a professional English writing coach.')
      setResult(r => ({ ...r, [lvl]: upgraded || text }))
    } catch { setResult(r => ({ ...r, [lvl]: 'تعذّر الاتصال.' })) }
    finally { setLoading(false) }
  }

  return (
    <div className="el-upgrade-section">
      <div className="el-upgrade-title">✨ ارتقِ بكتابتك — 3 مستويات</div>
      <div className="el-upgrade-levels">
        {[['b1','B1 — تحسين بسيط'],['b2','B2 — احترافي'],['c2','C2 — أكاديمي']].map(([k,lbl]) => (
          <button key={k} className={`el-upgrade-lvl-btn${level === k ? ' active' : ''}`} onClick={() => upgrade(k)}>{lbl}</button>
        ))}
      </div>
      {loading && level && !result[level] && <div style={{ textAlign: 'center', color: 'var(--el-muted)', padding: 10 }}>⏳ يُحسّن النص...</div>}
      {level && result[level] && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div className="el-upgrade-label" style={{ margin: 0 }}>📝 النص المُحسَّن ({level.toUpperCase()}):</div>
            <button className="el-nav-btn" style={{ fontSize: '.75rem', padding: '2px 10px' }}
              onClick={() => navigator.clipboard?.writeText(result[level])}>
              📋 نسخ
            </button>
          </div>
          <div className="el-upgrade-result">{result[level]}</div>
        </div>
      )}
    </div>
  )
}

/* ─── AI Live Reaction after Listening score ─── */
function AILiveReaction({ score }) {
  const reactions = [
    { min: 100, emoji: '🎉', text: 'واو! نتيجة مثالية! أنت تستمع بأذن موسيقار!', color: '#dcfce7', border: '#16a34a' },
    { min: 70,  emoji: '😊', text: 'أحسنت! أداء ممتاز — فقط بعض الكلمات تحتاج مزيداً من التركيز.', color: '#dbeafe', border: '#3b82f6' },
    { min: 40,  emoji: '💪', text: 'جيد! هذا المستوى جيد — راجع الأسئلة الخاطئة وحاول مجدداً.', color: '#fef3c7', border: '#f59e0b' },
    { min: 0,   emoji: '🤗', text: 'لا بأس! كل خطأ درس مهم. راجع النص جيداً وأعد المحاولة.', color: '#fee2e2', border: '#ef4444' },
  ]
  const r = reactions.find(rx => score.pct >= rx.min) || reactions[reactions.length - 1]
  return (
    <div className="el-reaction-box" style={{ background: r.color, borderColor: r.border }}>
      <div className="el-reaction-emoji">{r.emoji}</div>
      <div className="el-reaction-text">{r.text}</div>
    </div>
  )
}

/* ─── Vocabulary Story Generator ─── */
function _firstSentence(text) {
  // Extract first sentence and cap at ~120 chars
  const m = text.match(/^[^.!?]*[.!?]/)
  const s = m ? m[0].trim() : text.trim()
  return s.length > 140 ? s.slice(0, 137).replace(/\s\S+$/, '') + '...' : s
}

function _buildStory(genre, words) {
  const shuffled = [...words].sort(() => Math.random() - 0.5)
  const picked = shuffled.slice(0, Math.min(5, words.length))

  if (genre === 'حوار حياة يومية') {
    const speakers = ['Sarah', 'Mark']
    const lines = []
    lines.push({ speaker: 'Sarah', text: `I have been thinking about what we learned today — especially the word "${picked[0].word}".` })
    picked.forEach((w, i) => {
      const sp = speakers[i % 2]
      lines.push({ speaker: sp, text: _firstSentence(w.example) })
      if (i < picked.length - 1) {
        const other = speakers[(i + 1) % 2]
        lines.push({ speaker: other, text: `That is a great example of "${picked[i + 1]?.word || w.word}". Can you say more?` })
      }
    })
    lines.push({ speaker: 'Mark', text: `These are exactly the kinds of words that make the difference in ${words[0]?.arabic ? 'real communication' : 'English'}.` })
    return lines
  }

  if (genre === 'قصة مغامرة') {
    const lines = []
    lines.push({ speaker: 'Narrator', text: `The journey began unexpectedly — as the best adventures always do.` })
    picked.forEach((w, i) => {
      if (i % 2 === 0) {
        lines.push({ speaker: 'Narrator', text: _firstSentence(w.example) })
      } else {
        lines.push({ speaker: i % 4 === 1 ? 'Sarah' : 'Mark', text: _firstSentence(w.example) })
      }
    })
    lines.push({ speaker: 'Narrator', text: `And so they moved forward — carrying new understanding, and the words to express it.` })
    return lines
  }

  if (genre === 'تقرير إخباري') {
    const lines = []
    lines.push({ speaker: 'Reporter', text: `In today's report, we examine several key ideas that are shaping our world.` })
    picked.forEach((w, i) => {
      lines.push({ speaker: i % 2 === 0 ? 'Reporter' : 'Narrator', text: _firstSentence(w.example) })
    })
    lines.push({ speaker: 'Reporter', text: `These concepts — ${picked.map(w => `"${w.word}"`).join(', ')} — are essential for understanding today's issues. This has been your evening briefing.` })
    return lines
  }

  if (genre === 'قصة رومانسية') {
    const lines = []
    lines.push({ speaker: 'Sarah', text: `There are things I have never quite known how to say. But today I learned the right words.` })
    picked.forEach((w, i) => {
      lines.push({ speaker: i % 2 === 0 ? 'Sarah' : 'Mark', text: _firstSentence(w.example) })
    })
    lines.push({ speaker: 'Mark', text: `Knowing the right word for something — it changes how you see it entirely.` })
    lines.push({ speaker: 'Sarah', text: `Yes. And how you feel it, too.` })
    return lines
  }

  return picked.map((w, i) => ({ speaker: i % 2 === 0 ? 'Sarah' : 'Mark', text: _firstSentence(w.example) }))
}

const GENRES = ['حوار حياة يومية', 'قصة مغامرة', 'تقرير إخباري', 'قصة رومانسية']

function VocabStoryGen({ words, dayTitle, levelId, allLearnedWords = [] }) {
  const [open, setOpen] = useState(false)
  const [genre, setGenre] = useState('حوار حياة يومية')
  const [lines, setLines] = useState([])
  const [usedWords, setUsedWords] = useState([])

  const generate = () => {
    const story = _buildStory(genre, words)
    setLines(story)
    const allText = story.map(l => l.text).join(' ').toLowerCase()
    setUsedWords(words.filter(w => allText.includes(w.word.toLowerCase())).map(w => w.word))
  }

  const highlightLine = (text) => {
    if (!usedWords.length) return text
    const escaped = usedWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    const pattern = new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi')
    return text.split(pattern).map((part, i) =>
      usedWords.some(w => w.toLowerCase() === part.toLowerCase())
        ? <strong key={i} className="el-story-highlight">{part}</strong>
        : <span key={i}>{part}</span>
    )
  }

  const SPEAKER_COLORS = { Sarah: '#c9858a', Mark: '#3b82f6', Narrator: '#16a34a', Reporter: '#7c3aed' }

  return (
    <div className="el-story-section">
      <button className="el-roleplay-toggle" style={{ marginBottom: open ? 14 : 0 }} onClick={() => setOpen(o => !o)}>
        📖 {open ? 'إغلاق القصص النموذجية' : 'قصص نموذجية — شاهد مفردات اليوم في سياق'}
      </button>
      {open && (
        <>
          <div className="el-story-title">اختر نوع القصة:</div>
          <div className="el-story-settings">
            {GENRES.map(g => (
              <button key={g} className={`el-story-setting-btn${genre === g ? ' active' : ''}`} onClick={() => setGenre(g)}>{g}</button>
            ))}
          </div>
          <button className="el-nav-btn primary" onClick={generate}>
            ✨ اعرض قصة
          </button>
          {lines.length > 0 && (
            <>
              <div style={{ display: 'flex', gap: 8, margin: '10px 0 4px', flexWrap: 'wrap' }}>
                <DualSpeak text={lines.map(l => l.speaker ? `${l.speaker} says: ${l.text}` : l.text).join('. ')} />
                <button className="el-speak-btn" style={{ fontSize: '.85rem', padding: '4px 10px', borderRadius: 8 }}
                  onClick={() => stopTTS()}>⏹ أوقف</button>
              </div>
              <div className="el-story-dialogue">
                {lines.map((l, i) => (
                  <div key={i} className={`el-story-line${l.speaker ? ' has-speaker' : ''}`}
                    style={{ '--speaker-color': SPEAKER_COLORS[l.speaker] || '#6366f1' }}>
                    {l.speaker && <span className="el-story-speaker">{l.speaker}</span>}
                    <span className="el-story-line-text">{highlightLine(l.text)}</span>
                    <DualSpeak text={l.text} style={{ marginRight: 6, flexShrink: 0 }} />
                  </div>
                ))}
              </div>
              <div className="el-story-words-used">
                {usedWords.length > 0
                  ? <>✅ الكلمات المستخدمة: {usedWords.map((w, i) => <strong key={i}>{w}{i < usedWords.length-1 ? '، ' : ''}</strong>)}</>
                  : <span style={{ color: 'var(--el-muted)', fontSize: '.82rem' }}>لم تُستخدم كلمات اليوم مباشرةً في هذه القصة — جرّب نوعاً آخر!</span>
                }
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

/* ─── Fill-the-Gap Exercise ─── */
function _fillGapShuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[a[i], a[j]] = [a[j], a[i]] }
  return a
}

function FillGapExercise({ words, allLearnedWords = [], levelId, dayId }) {
  const [open, setOpen] = useState(false)
  const [useAll, setUseAll] = useState(false)
  const [answers, setAnswers] = useState({})
  const [checked, setChecked] = useState(false)
  const pool = useAll && allLearnedWords.length >= 4 ? allLearnedWords : words

  const authored = getFillGapSentences(levelId, dayId)

  // Word-bank style (not free typing): each sentence gets the correct word +
  // 3 distractors from the same pool, so there's never more than one option
  // that's actually "the expected answer" — no ambiguity from synonyms,
  // typos, or grammatically-valid-but-wrong words.
  const sentences = useMemo(() => {
    if (authored && !useAll) {
      const answerSet = [...new Set(authored.map(s => s.answer))]
      return authored.map(s => {
        const distractorPool = answerSet.filter(a => a !== s.answer)
        const distractors = _fillGapShuffle(distractorPool).slice(0, 3)
        return {
          sentence: s.sentence,
          answer: s.answer,
          arabic: s.arabic,
          options: _fillGapShuffle([s.answer, ...distractors]),
        }
      })
    }
    const eligible = pool.filter(w => w.example && new RegExp(`\\b${w.word}\\b`, 'i').test(w.example))
    return eligible.slice(0, 6).map(w => {
      const distractorPool = eligible.filter(x => x.word.toLowerCase() !== w.word.toLowerCase())
      const distractors = _fillGapShuffle(distractorPool).slice(0, 3).map(x => x.word)
      return {
        sentence: w.example.replace(new RegExp(`\\b${w.word}\\b`, 'i'), '_____'),
        answer: w.word,
        arabic: w.arabic,
        options: _fillGapShuffle([w.word, ...distractors]),
      }
    })
  }, [pool, authored, useAll])

  const score = sentences.filter((s, i) => answers[i] === s.answer).length

  return (
    <div className="el-fillgap-section">
      <button className="el-roleplay-toggle" style={{ background:'#f0fdf4', borderColor:'#86efac', color:'#15803d', marginBottom: open ? 14 : 0 }} onClick={() => setOpen(o => !o)}>
        {open ? 'أغلق التمرين' : 'Fill-the-Gap — أكمل الفراغ من كلمات اليوم'}
      </button>
      {open && (
        <>
          {allLearnedWords.length >= 4 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button className={`el-family-chip${!useAll ? ' active' : ''}`} onClick={() => { setUseAll(false); setAnswers({}); setChecked(false) }}>كلمات اليوم</button>
              <button className={`el-family-chip${useAll ? ' active' : ''}`} onClick={() => { setUseAll(true); setAnswers({}); setChecked(false) }}>كل كلماتي ({allLearnedWords.length})</button>
            </div>
          )}
          {sentences.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--el-muted)', padding: '12px 0', fontSize: '.85rem' }}>
              لا توجد جمل كافية لهذا التمرين. حاول مع "كل كلماتي" إذا كنت قد تعلّمت كلمات سابقة.
            </div>
          )}
          {sentences.map((s, i) => {
            const selected = answers[i]
            const correct = selected === s.answer
            const parts = s.sentence.split('_____')
            return (
              <div key={i} className="el-fillgap-item">
                <div className="el-fillgap-sentence">
                  {parts[0]}
                  <span className={`el-fillgap-blank${checked ? (correct ? ' correct' : ' wrong') : selected ? ' filled' : ''}`}>
                    {selected || '_____'}
                  </span>
                  {parts[1]}
                </div>
                <div className="el-fillgap-options">
                  {s.options.map(opt => (
                    <button
                      key={opt}
                      className={`el-family-chip${selected === opt ? ' active' : ''}`}
                      onClick={() => !checked && setAnswers(a => ({ ...a, [i]: opt }))}
                      disabled={checked}
                    >{opt}</button>
                  ))}
                </div>
                {checked && !correct && <div className="el-fillgap-hint">الصواب: <strong>{s.answer}</strong> ({s.arabic})</div>}
              </div>
            )
          })}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {!checked
              ? <button className="el-nav-btn primary" onClick={() => setChecked(true)} disabled={Object.keys(answers).length < sentences.length}>تحقق</button>
              : <><div className="el-fillgap-score">{score === sentences.length ? 'ممتاز! كل الإجابات صحيحة ✅' : `${score} / ${sentences.length} صحيحة`}</div><button className="el-nav-btn" onClick={() => { setAnswers({}); setChecked(false) }}>مرة أخرى</button></>
            }
          </div>
        </>
      )}
    </div>
  )
}

/* ─── Dialogue Partner — real turn-based conversation ─── */
function DialoguePartner({ day }) {
  const { shadowing: s } = day
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(0)
  const [aPlayed, setAPlayed] = useState(false)
  const [recording, setRecording] = useState(false)
  const [userAudio, setUserAudio] = useState(null)
  const [done, setDone] = useState(false)
  const mrRef = useRef(null)
  const chunksRef = useRef([])

  // Build dialogue lines from shadowing data
  const lines = (() => {
    if (s.dialogue && Array.isArray(s.dialogue)) return s.dialogue
    // Auto-build a short 4-line dialogue from the shadowing chunk
    return [
      { role: 'A', text: s.chunk },
      { role: 'B', text: s.chunk },
      { role: 'A', text: s.steps?.[1] || `Can you say it again? — ${s.chunk}` },
      { role: 'B', text: s.chunk },
    ]
  })()

  const current = lines[step] || lines[lines.length - 1]
  const isMyTurn = current.role === 'B'

  // Auto-play A's line when step changes to A
  useEffect(() => {
    if (!open || isMyTurn) return
    setAPlayed(false)
    setUserAudio(null)
    const t = setTimeout(() => {
      speakAtRate(current.text, 0.85, 'en-US', () => setAPlayed(true))
      setAPlayed(true)
    }, 400)
    return () => clearTimeout(t)
  }, [step, open]) // eslint-disable-line

  const startRecording = async () => {
    if (recording) { mrRef.current?.stop(); return }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      chunksRef.current = []
      const mr = new MediaRecorder(stream)
      mr.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        setUserAudio(URL.createObjectURL(blob))
        stream.getTracks().forEach(t => t.stop())
        setRecording(false)
        track('dialogue_partner_used')
      }
      mrRef.current = mr
      setRecording(true)
      mr.start()
    } catch { alert('لم يُمنح إذن الميكروفون') }
  }

  const nextStep = () => {
    if (step + 1 >= lines.length) { setDone(true); return }
    setStep(s => s + 1)
    setUserAudio(null)
    setAPlayed(false)
  }

  const restart = () => {
    stopTTS()
    setStep(0); setDone(false); setUserAudio(null); setAPlayed(false); setRecording(false)
  }

  return (
    <div className="el-dialogue-section">
      <button className="el-roleplay-toggle" style={{ marginBottom: open ? 14 : 0 }}
        onClick={() => { setOpen(o => !o); if (open) { stopTTS(); restart() } }}>
        🎙️ {open ? 'إغلاق Dialogue Partner' : 'Dialogue Partner — محادثة حقيقية'}
      </button>
      {open && (
        <>
          <div style={{ fontSize: '.82rem', color: 'var(--el-muted)', marginBottom: 12 }}>
            أنت الدور <strong style={{ color: '#22c55e' }}>B</strong> — المساعد يلعب دور <strong>A</strong> ويتكلم تلقائياً 🤖
          </div>

          {done ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>🎉</div>
              <div style={{ fontWeight: 700, color: 'var(--el-accent)', marginBottom: 12 }}>أنهيت المحادثة! ممتاز!</div>
              <button className="el-nav-btn primary" onClick={restart}>🔄 أعد المحادثة</button>
            </div>
          ) : (
            <>
              {/* Conversation history */}
              <div className="el-dialogue-script" style={{ marginBottom: 12 }}>
                {lines.slice(0, step).map((line, i) => (
                  <div key={i} className={`el-dialogue-line${line.role === 'B' ? ' student-role' : ''}`}>
                    <div className="el-dialogue-role">{line.role}</div>
                    <div className="el-dialogue-text" style={{ opacity: .6 }}>{line.text}</div>
                  </div>
                ))}
              </div>

              {/* Current line */}
              <div className={`el-dialogue-line${isMyTurn ? ' student-role' : ''}`}
                style={{ border: '2px solid var(--el-accent)', borderRadius: 12, padding: '10px 14px', marginBottom: 14 }}>
                <div className="el-dialogue-role" style={{ fontSize: '1rem', fontWeight: 800 }}>{current.role}</div>
                <div className="el-dialogue-text" style={{ fontSize: '1.05rem' }}>
                  {isMyTurn ? current.text : current.text}
                </div>
                {!isMyTurn && (
                  <button className="el-speak-btn" style={{ marginRight: 8 }}
                    onClick={() => speakAtRate(current.text, 0.85, 'en-US', () => setAPlayed(true))}>
                    🔊
                  </button>
                )}
              </div>

              {isMyTurn ? (
                /* B turn — user records */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                  <div style={{ fontSize: '.85rem', color: 'var(--el-muted)' }}>دورك! سجّل صوتك وأنت تقول هذه الجملة:</div>
                  <button
                    className={`el-shadow-rec-btn${recording ? ' recording' : ''}`}
                    style={{ fontSize: '1rem', padding: '10px 28px', borderRadius: 14 }}
                    onClick={startRecording}
                  >
                    {recording ? '⏹ أوقف التسجيل' : '🎤 سجّل دورك'}
                  </button>
                  {userAudio && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                      <span style={{ fontSize: '.8rem', color: 'var(--el-accent)' }}>🔁 صوتك:</span>
                      <audio src={userAudio} controls style={{ height: 28, flex: 1 }} />
                    </div>
                  )}
                  <button
                    className="el-nav-btn primary"
                    disabled={!userAudio && !recording}
                    onClick={nextStep}
                    style={{ marginTop: 4 }}
                  >
                    {step + 1 >= lines.length ? '✅ انتهت المحادثة' : 'التالي →'}
                  </button>
                </div>
              ) : (
                /* A turn — wait for TTS */
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                  <div style={{ fontSize: '.85rem', color: 'var(--el-muted)' }}>
                    {aPlayed ? 'استمعت؟ تابع →' : '🔊 المساعد يتكلم...'}
                  </div>
                  <button className="el-nav-btn primary" onClick={nextStep}>
                    {aPlayed ? 'التالي →' : 'تخطّى →'}
                  </button>
                </div>
              )}

              <div style={{ textAlign: 'center', marginTop: 10, fontSize: '.78rem', color: 'var(--el-muted)' }}>
                السطر {step + 1} / {lines.length}
              </div>
              <button className="el-nav-btn" style={{ display: 'block', margin: '8px auto 0', fontSize: '.78rem' }} onClick={restart}>
                🔄 أعد من البداية
              </button>
            </>
          )}
        </>
      )}
    </div>
  )
}

/* ─── Grammar Pattern Flashcards ─── */
function GrammarPatternFlashcards({ patterns }) {
  const [open, setOpen] = useState(false)
  const [idx, setIdx] = useState(0)
  const [flipped, setFlipped] = useState(false)
  if (!patterns || !patterns.length) return null
  const card = patterns[idx % patterns.length]

  return (
    <div className="el-gflash-section">
      <button className="el-roleplay-toggle" style={{ background:'#ede9fe', borderColor:'#a78bfa', color:'#5b21b6', marginBottom: open ? 14 : 0 }} onClick={() => setOpen(o => !o)}>
        {open ? 'أغلق بطاقات القواعد' : 'Grammar Flashcards — احفظ القاعدة بالبطاقة'}
      </button>
      {open && (
        <div className="el-gflash-wrap">
          <div className={`el-gflash-card${flipped ? ' flipped' : ''}`} onClick={() => setFlipped(f => !f)}>
            <div className="el-gflash-inner">
              <div className="el-gflash-front">
                <div className="el-gflash-name">{card.name}</div>
                <div style={{ fontSize: '.78rem', color: 'var(--el-muted)', marginTop: 8 }}>اضغط لرؤية الصيغة</div>
              </div>
              <div className="el-gflash-back">
                <div className="el-gflash-formula">{card.formula}</div>
                <div className="el-gflash-ex">{card.examples?.[0]}</div>
              </div>
            </div>
          </div>
          <div className="el-gflash-nav">
            <button className="el-nav-btn" onClick={() => { setIdx(i => Math.max(0, i - 1)); setFlipped(false) }}>←</button>
            <span>{(idx % patterns.length) + 1} / {patterns.length}</span>
            <button className="el-nav-btn" onClick={() => { setIdx(i => i + 1); setFlipped(false) }}>→</button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Writing Prompt Generator ─── */
const WRITING_PROMPTS_BANK = [
  [
    { level: 'B1', prompt: 'Write 3 sentences describing your daily routine using simple present tense.' },
    { level: 'B2', prompt: 'Write a short paragraph (80-100 words) about a challenge you faced and how you solved it.' },
    { level: 'C2', prompt: 'Write a critical analysis (150+ words) comparing two different perspectives on modern technology in education.' },
  ],
  [
    { level: 'B1', prompt: 'Write 3 sentences about your favorite place and why you like it.' },
    { level: 'B2', prompt: 'Describe a memorable event from your life in a well-structured paragraph.' },
    { level: 'C2', prompt: 'Discuss the ethical implications of artificial intelligence in healthcare using academic language.' },
  ],
  [
    { level: 'B1', prompt: 'Write about what you did last weekend using past tense.' },
    { level: 'B2', prompt: 'Write a formal email to a colleague explaining why you will be absent from a meeting.' },
    { level: 'C2', prompt: 'Evaluate the statement: "Social media has done more harm than good to society." Use evidence and counterarguments.' },
  ],
  [
    { level: 'B1', prompt: 'Describe your ideal home in 3 sentences.' },
    { level: 'B2', prompt: 'Write about a skill you want to learn and explain how you plan to achieve it.' },
    { level: 'C2', prompt: 'Argue for or against the proposition that globalization has eroded cultural identity.' },
  ],
]

function WritingPromptCard({ dayTitle }) {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * WRITING_PROMPTS_BANK.length))
  const prompts = WRITING_PROMPTS_BANK[idx]
  const colors = { B1: '#22c55e', B2: '#3b82f6', C2: '#8b5cf6' }

  return (
    <div className="el-wprompt-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: '.88rem' }}>أفكار للكتابة</div>
        <button className="el-nav-btn" style={{ fontSize: '.78rem', padding: '4px 10px' }}
          onClick={() => setIdx(i => (i + 1) % WRITING_PROMPTS_BANK.length)}>
          أفكار جديدة
        </button>
      </div>
      {prompts.map((p, i) => (
        <div key={i} className="el-wprompt-card" style={{ borderColor: colors[p.level] || '#ccc' }}>
          <span className="el-wprompt-level" style={{ background: colors[p.level] || '#ccc' }}>{p.level}</span>
          <span className="el-wprompt-text">{p.prompt}</span>
        </div>
      ))}
    </div>
  )
}

/* ─── Reading Bookmarks ─── */
function ReadingBookmarks({ levelId, dayId }) {
  const BOOK_KEY = `el_bookmarks_${levelId}_${dayId}`
  const loadBM = () => { try { return JSON.parse(localStorage.getItem(BOOK_KEY) || '[]') } catch { return [] } }
  const [bookmarks, setBookmarks] = useState(loadBM)
  const [input, setInput] = useState('')

  const add = () => {
    if (!input.trim()) return
    const next = [...bookmarks, { text: input.trim(), date: Date.now() }]
    setBookmarks(next); localStorage.setItem(BOOK_KEY, JSON.stringify(next)); setInput('')
  }
  const remove = (i) => {
    const next = bookmarks.filter((_, bi) => bi !== i)
    setBookmarks(next); localStorage.setItem(BOOK_KEY, JSON.stringify(next))
  }

  return (
    <div className="el-bmark-section">
      <div className="el-bmark-title">محفوظاتي من هذه القراءة</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <input className="el-buddy-input" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()} placeholder="احفظي جملة مهمة..." style={{ flex: 1 }} />
        <button className="el-buddy-send" onClick={add}>+</button>
      </div>
      {bookmarks.map((b, i) => (
        <div key={i} className="el-bmark-item">
          <span className="el-bmark-text">"{b.text}"</span>
          <button className="el-bmark-del" onClick={() => remove(i)}>x</button>
        </div>
      ))}
    </div>
  )
}

/* ─── Pronunciation Recorder ─── */
function PronunciationRecorder({ words, allLearnedWords = [] }) {
  const [open, setOpen] = useState(false)
  const [useAll, setUseAll] = useState(false)
  const [selected, setSelected] = useState(null)
  const [recorded, setRecorded] = useState('')
  const [confidence, setConfidence] = useState(0)
  const [listening, setListening] = useState(false)
  const [result, setResult] = useState(null)
  const [diffFeedback, setDiffFeedback] = useState('')
  const pool = useAll && allLearnedWords.length > 0 ? allLearnedWords : words

  const lev = (a, b) => {
    const dp = Array.from({length:a.length+1},(_,i)=>Array.from({length:b.length+1},(_,j)=>i===0?j:j===0?i:0))
    for(let i=1;i<=a.length;i++) for(let j=1;j<=b.length;j++) dp[i][j]=a[i-1]===b[j-1]?dp[i-1][j-1]:1+Math.min(dp[i-1][j],dp[i][j-1],dp[i-1][j-1])
    return dp[a.length][b.length]
  }

  const analyzeDiff = (heard, target) => {
    // Find first differing position
    const h = heard.toLowerCase(), t = target.toLowerCase()
    let firstDiff = -1
    for (let i = 0; i < Math.max(h.length, t.length); i++) {
      if (h[i] !== t[i]) { firstDiff = i; break }
    }
    if (firstDiff === -1) return ''
    const targetChar = t[firstDiff] || '(نهاية الكلمة)'
    const heardChar = h[firstDiff] || '(نهاية الكلمة)'
    // Map common English phoneme mistakes
    const hints = {
      'th': 'ضعي طرف لسانك بين أسنانك للصوت /θ/',
      'v': 'الشفة السفلى تلمس الأسنان العليا للصوت /v/',
      'p': 'أغلقي شفتيك ثم أفتحيهما بقوة للصوت /p/',
      'b': 'مثل /p/ لكن مع اهتزاز الحلق',
      'r': 'لفّي اللسان للخلف قليلاً للصوت /r/ الأمريكي',
      'w': 'ابدئي بضم الشفتين للصوت /w/',
    }
    const hint = hints[targetChar] || hints[t.slice(firstDiff, firstDiff+2)] || `ركزي على الحرف "${targetChar}" في موضع ${firstDiff + 1}`
    return hint
  }

  const record = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) { alert('يحتاج Chrome أو Edge'); return }
    const r = new SR(); r.lang='en-US'; r.interimResults=false; r.maxAlternatives=3
    r.onstart=()=>setListening(true)
    r.onresult=e=>{
      // Pick best matching alternative
      const alts = Array.from(e.results[0]).map(a => a.transcript.toLowerCase().trim())
      const target = (selected?.word||'').toLowerCase()
      const best = alts.reduce((a, b) => lev(a, target) <= lev(b, target) ? a : b)
      const conf = e.results[0][0].confidence
      setRecorded(best)
      setConfidence(Math.round(conf * 100))
      const dist = lev(best, target)
      const pct = 1 - dist / Math.max(target.length, 1)
      if (dist === 0) { setResult('match'); setDiffFeedback('') }
      else if (pct >= 0.7 || best.includes(target) || target.includes(best)) { setResult('close'); setDiffFeedback(analyzeDiff(best, target)) }
      else { setResult('try'); setDiffFeedback(analyzeDiff(best, target)) }
      setListening(false)
    }
    r.onerror = (e) => {
      setListening(false)
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        alert('لم يُمنح إذن الميكروفون — تأكدي من السماح للموقع باستخدام الميكروفون من إعدادات المتصفح/الجهاز 🎤')
      } else if (e.error === 'no-speech') {
        alert('ما سمعنا صوت — جرّبي تحكي أقرب للميكروفون')
      } else if (e.error === 'network') {
        alert('التعرف على الصوت يحتاج اتصال إنترنت — تأكدي من الشبكة')
      }
    }
    r.onend = () => setListening(false)
    r.start()
  }

  const speakWord = (rate, lang = 'en-US') => speakAtRate(selected.word, rate, lang)

  return (
    <div className="el-pronrec-section">
      <button className="el-roleplay-toggle" style={{ background:'#fce7f3', borderColor:'#f9a8d4', color:'#9d174d', marginBottom: open ? 14 : 0 }} onClick={()=>setOpen(o=>!o)}>
        {open ? 'اغلق تسجيل النطق' : 'Pronunciation Recorder — سجلي نطقك واعرفي أي صوت أخطأتِ'}
      </button>
      {open && (
        <>
          {allLearnedWords.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button className={`el-family-chip${!useAll ? ' active' : ''}`} onClick={() => { setUseAll(false); setSelected(null); setResult(null) }}>كلمات اليوم</button>
              <button className={`el-family-chip${useAll ? ' active' : ''}`} onClick={() => { setUseAll(true); setSelected(null); setResult(null) }}>كل كلماتي ({allLearnedWords.length})</button>
            </div>
          )}
          <div className="el-family-pick">
            {pool.slice(0,16).map((w,i)=>(
              <button key={i} className={`el-family-chip${selected?.word===w.word?' active':''}`}
                onClick={()=>{setSelected(w);setRecorded('');setResult(null);setDiffFeedback('')}}>{w.word}</button>
            ))}
          </div>
          {selected && (
            <div className="el-pronrec-card">
              <div className="el-pronrec-word">{selected.word}</div>
              <div className="el-pronrec-ipa">{selected.ipa}</div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center', margin: '8px 0', flexWrap: 'wrap' }}>
                <button className="el-speak-btn" style={{fontSize:'.9rem'}} onClick={() => speakWord(0.6, 'en-US')}>🇺🇸 بطيء</button>
                <button className="el-speak-btn" style={{fontSize:'.9rem'}} onClick={() => speakWord(0.9, 'en-US')}>🇺🇸 عادي</button>
                <button className="el-speak-btn" style={{fontSize:'.9rem'}} onClick={() => speakWord(0.6, 'en-GB')}>🇬🇧 بطيء</button>
                <button className="el-speak-btn" style={{fontSize:'.9rem'}} onClick={() => speakWord(0.9, 'en-GB')}>🇬🇧 عادي</button>
              </div>
              <button className={`el-pronrec-btn${listening?' recording':' record'}`} onClick={record} disabled={listening}>
                {listening ? '🔴 يسجل — تكلمي الآن...' : '🎤 سجلي نطقك'}
              </button>
              {recorded && (
                <div className="el-pronrec-result">
                  <div style={{ fontSize: '.85rem', color: 'var(--el-text2)', marginBottom: 6 }}>
                    سمعت: <strong>"{recorded}"</strong>
                    {confidence > 0 && <span style={{ marginRight: 8, color: 'var(--el-muted)', fontSize: '.78rem' }}>({confidence}% وضوح)</span>}
                  </div>
                  {result==='match' && <div className="el-pronrec-feedback match">✅ نطق مثالي! أحسنتِ تماماً</div>}
                  {(result==='close' || result==='try') && (
                    <div className={`el-pronrec-feedback ${result === 'close' ? 'close' : 'tryagain'}`}>
                      {result === 'close' ? '🟡 قريب جداً — الحرف الأحمر هو موضع الخطأ:' : '🔴 جربي مرة أخرى — شاهدي أين الخطأ:'}
                      <div className="el-pronrec-diff">
                        <div>
                          <span style={{ fontSize: '.75rem', color: 'var(--el-muted)' }}>🎯 الصواب:</span>{' '}
                          <span style={{ fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: 2 }}>
                            {selected.word.toLowerCase().split('').map((char, i) => {
                              const heardChar = recorded.toLowerCase()[i]
                              const wrong = heardChar !== char
                              return (
                                <span key={i} style={{
                                  background: wrong ? '#fecaca' : 'transparent',
                                  color: wrong ? '#dc2626' : 'inherit',
                                  fontWeight: wrong ? 'bold' : 'normal',
                                  borderBottom: wrong ? '2px solid #dc2626' : 'none',
                                  padding: '0 1px', borderRadius: 2
                                }}>{char}</span>
                              )
                            })}
                            {recorded.length > selected.word.length && (
                              <span style={{ background: '#fee2e2', color: '#dc2626', textDecoration: 'line-through', padding: '0 1px' }}>
                                {recorded.toLowerCase().slice(selected.word.length)}
                              </span>
                            )}
                          </span>
                        </div>
                        <div>
                          <span style={{ fontSize: '.75rem', color: 'var(--el-muted)' }}>👂 سمعت:</span>{' '}
                          <span style={{ fontFamily: 'monospace', fontSize: '1.1rem', letterSpacing: 2 }}>{recorded}</span>
                        </div>
                      </div>
                      {diffFeedback && <div className="el-pronrec-hint">💡 {diffFeedback}</div>}
                      {result === 'try' && (
                        <div style={{ fontSize: '.8rem', marginTop: 4, color: 'var(--el-muted)' }}>
                          IPA: {selected.ipa} — استمعي للنموذج بشكل بطيء ثم كرري
                        </div>
                      )}
                    </div>
                  )}
                  <button className="el-nav-btn" style={{ marginTop: 8, fontSize: '.8rem' }}
                    onClick={() => { setRecorded(''); setResult(null); setDiffFeedback('') }}>
                    🔄 حاولي مرة أخرى
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* ─── XP Pop Animation ─── */
export function XPPopAnimation({ onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 1800); return () => clearTimeout(t) }, [onDone])
  return (
    <div className="el-xpop-overlay">
      <div className="el-xpop-bubble">
        <div className="el-xpop-icon">✅</div>
        <div className="el-xpop-amount">أحسنت! أنهيت درسك</div>
      </div>
    </div>
  )
}

/* ─── Study Timer hook ─── */
export function useStudyTimer() {
  const KEY = 'el_study_time'
  const today = new Date().toISOString().slice(0, 10)
  const loadTimer = () => { try { return JSON.parse(localStorage.getItem(KEY) || '{}') } catch { return {} } }
  const [timerData, setTimerData] = useState(loadTimer)
  const addTime = useCallback((seconds) => {
    setTimerData(prev => {
      const next = { ...prev, [today]: (prev[today] || 0) + seconds }
      localStorage.setItem(KEY, JSON.stringify(next))
      return next
    })
  }, [today])
  const todayMinutes = Math.floor((timerData[today] || 0) / 60)
  const weekMinutes = Math.floor(Object.entries(timerData).filter(([d]) => new Date(d) >= new Date(Date.now() - 7*86400000)).reduce((s,[,v])=>s+v, 0) / 60)
  return { addTime, todayMinutes, weekMinutes, timerData }
}

